import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { BookOpen, RotateCcw, Dumbbell, User as UserIcon } from 'lucide-react';
import './App.css';
import { ThemeProvider } from './components/ThemeContext';
import Privacy from './components/Privacy';
import Terms from './components/Terms';
import About from './components/About';
import GuideWolof from './components/GuideWolof';
import GuideBambara from './components/GuideBambara';
import GuideSwahili from './components/GuideSwahili';
import NotFound from './components/NotFound';
import MobileFooter from './components/MobileFooter';
import BugReportButton from './components/BugReportButton';



// Languages import
// Note: wolof importé directement pour l'état initial (default). Les autres langues
// sont chargées via getLanguageDataAsync (chunks Webpack séparés par langue).
import wolofDefaultData from './data/languages/wolof';
import { getLanguageDataAsync } from './data/languages';
import { type LanguageData, type WordData } from './types/types';

import { resetProgress } from './utils/resetProgress';

// Components imports
import './components/QuizComponent/QuizNavigationButtons.css';
import LearnMode from './components/LearnMode';
import CategorySelection, { UNIT_CONFIGS } from './components/CategorySelection';
import { splitIntoParts, TRAIL_SPLIT_SIZE } from './utils/trailSplit';
import { buildTrailSequence, findTrailStepIndex, nextTrailStep, type TrailNavStep } from './utils/trailSequence';
import SentenceConstructionGame from './components/SentenceConstructionGame';
import QuizComponent from './components/QuizComponent/QuizComponent';
import SentenceGapGame from './components/SentenceGapGame';
import CelebrationAnimation from './components/CelebrationAnimation';
import CategoryCelebration from './components/CategoryCelebration';
import { AnimatePresence } from 'framer-motion';
import InteractiveMascot from './components/InteractiveMascotte';
import MainMenu, { MainTab } from './components/MainMenu';

import GrammarMode from './components/GrammarMode';

import ConfirmationModal from './components/ConfirmationModal';
import RevisionMode from './components/RevisionMode/RevisionMode';
import LandingPage from './components/LandingPage';
import MobileLoginPage from './components/MobileLoginPage';
import ProfilePage from './components/ProfilePage';
import ChallengeMode from './components/ChallengeMode';
import MiniGames from './components/MiniGames';
import ConversationGame from './components/ConversationGame';
import SongAnalyzer from './components/SongAnalyzer';
import WordleGame from './components/WordleGame';
import MemoryDuelGame from './components/MemoryDuelGame';
import ForgeGame from './components/ForgeGame';
import TisserandGame from './components/TisserandGame';
import OnboardingModal, { ONBOARDING_DONE_KEY, syncOnboardingToAccount } from './components/OnboardingModal';
import { syncXP, syncStreak, getMyProfile } from './services/friendsService';
import { getPendingChallenges } from './services/challengeService';
import { recordActivity, isStreakCountedToday, SESSION_DATE, isStreakSavable, computeRestoredStreak } from './hooks/useDailyStreak';



import { 
  getLastUsedLanguage, 
  saveLastUsedLanguage, 
  
  markAppAsUsed,
  getLanguageInfo
} from './constants/languages';


import supabaseProgressService from './services/supabaseProgressService';
import { supabase } from './services/supabaseAudioService';
import { Capacitor } from '@capacitor/core';
import { initAdMob, showSessionAd } from './services/admobService';
import { markFirstSeen, shouldAskForReview, requestAppReview } from './services/appReviewService';
import revenueCatService from './services/revenueCatService';

import './styles/responsive.css';

import './components/SwipeNavigationManager.css';
import ExerciseMode from './components/ExerciseMode';
import KamaSigneDirecteurExercise from './components/kama/KamaSigneDirecteurExercise';
import { signeDirecteurPractice } from './data/languages/kama/signeDirecteurPractice';
import { graphieReconstructionPractice } from './data/languages/kama/graphieReconstructionPractice';
import {
  KAMA_NON_VOCAB_CATEGORIES as KAMA_NON_VOCAB_CATEGORIES_SHARED,
  KAMA_NON_VOCAB_KEYS_IN_LECTURE as KAMA_NON_VOCAB_KEYS_IN_LECTURE_SHARED,
} from './data/languages/kama/nonVocab';

import SubscriptionModal from './components/SubscriptionModal';
import subscriptionService, { SubscriptionTier,  } from './services/subscriptionService';
import paymentService from './services/paymentService';

import { Route, Routes, useLocation } from 'react-router-dom';
import PaymentSuccessPage from './components/PaymentSuccessPage';

// Utils and types imports
import { transformCategories } from './utils/utils';

import { 
  
  AppMode, 
  UserProgress, 
  LearnedWord, 
  LanguageCode, 
  Word,
 
  serializeUserProgress,
  deserializeUserProgress,
  SerializedUserProgress
} from './types/types';

//  CORRECTION 1 : Import correct de useRevisionProgress
import { useRevisionProgress } from './hooks/useRevisionProgress';



import MobileHeader from './components/MobileHeader';
import { cleanParentheses } from './utils/cleanParentheses';
import LanguageFirstSelection from './constants/LanguageFirstSelection';
import LevelUpAnimation from './components/LevelUpAnimation';
import DailyStreakAnimation from './components/DailyStreakAnimation';
import PremiumUnlockedAnimation from './components/PremiumUnlockedAnimation';
import CollectibleUnlockedAnimation from './components/CollectibleUnlockedAnimation';
import { getCollectibles, getUnlockedIds, CollectibleDef } from './utils/collectibles';
import { getLevel, LevelTitle } from './data/levelTitles';
import { awardXP, flushXPQueue, clearCachedLifetimeXP, XP_SOURCES, XpSource } from './services/xpService';
import { initNotifications, registerServiceWorker, enableChallengePush, syncWebPush, unregisterPushOnLogout, CHALLENGE_TAP_EVENT } from './services/notificationService';
import NotificationPermissionBanner from './components/NotificationPermissionBanner';
import CookieConsentBanner from './components/CookieConsentBanner';
import AndroidAppBanner from './components/AndroidAppBanner';
import ShareProgressModal from './components/ShareProgressModal';
import type { ShareCardData } from './utils/generateShareCard';

import { Picto } from './components/common/Picto';

// Marqueur passé à handleSuccess pour sauter la célébration générique.
const NO_CELEBRATION = Symbol('no-celebration');
// Pages « Apprendre » chargées à la demande : elles embarquent le vocabulaire
// de cinq langues, inutile dans le bundle principal de l'application.
const LearnRoutes = React.lazy(() => import('./components/learn/LearnPages'));
const ArticleRoutes = React.lazy(() => import('./components/articles/ArticlePages'));

const CORRECT_ANSWERS_TO_LEARN = 3;





function removeWhiteHeader() {
 
  const whiteHeaders = document.querySelectorAll('.white-header-element, .header-spacer');
  whiteHeaders.forEach(el => {
    if (el) el.remove();
  });
  
  // S'assurer que la classe mobile-view est bien appliquee
  if (window.innerWidth <= 768) {
    document.body.classList.add('mobile-view');
    
    // Appliquer des styles supplementaires
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.paddingTop = '60px';
    document.body.style.overflowX = 'clip';
  }
}

// AJOUT: Fonction de mappage mode -> onglet
const getModeTab = (mode: AppMode): MainTab => {
  switch(mode) {
    case 'learn':
      return 'learn';
    case 'review':
      return 'review';
    case 'quiz':
    case 'sentenceGap':
    case 'exercise':
      return 'games';
    case 'challenge':
    case 'minigames':
    case 'dialogue':
      return 'learn';
    case 'profile':
      return 'profile';
    case 'grammar':
    case 'sentenceConstruction':
      return 'learn'; // Ces modes appartiennent a  l'onglet learn
    default:
      return 'learn';
  }
};


// Remet le streak à 0 au démarrage s'il est réellement perdu : plus de jours
// manqués que la grâce (useDailyStreak) et pas assez de boucliers pour le sauver.
// Sinon on garde le compteur : computeDailyStreak() consommera les boucliers à
// la prochaine session (remis à 0 ici, ils n'étaient jamais utilisés).
(() => {
  try {
    const raw = localStorage.getItem('jogolinga-daily-streak');
    if (!raw) return;
    const data = JSON.parse(raw);
    if (!isStreakSavable(data.date)) {
      localStorage.setItem('jogolinga-daily-streak', JSON.stringify({ date: data.date, streak: 0 }));
    }
  } catch { /* ignore */ }
})();

const addToSyncQueue = (
  languageCode: LanguageCode,
  progress: UserProgress,
  categories: string[],
  totalXP: number
) => {
  try {
    const queue = JSON.parse(localStorage.getItem('sync-queue') || '[]');
    
    // Retirer l'ancienne entree pour cette langue si elle existe
    const filtered = queue.filter((item: any) => item.languageCode !== languageCode);
    
    // Ajouter la nouvelle entree
    filtered.push({
      languageCode,
      progress: serializeUserProgress(progress),
      categories,
      totalXP,
      timestamp: Date.now(),
      attempts: 0
    });
    
    localStorage.setItem('sync-queue', JSON.stringify(filtered));
   
  } catch (error) {
}
};

// Supprime toutes les données de progression du localStorage (toutes langues).
// À appeler au logout et avant de charger la progression d'un utilisateur connecté,
// pour éviter qu'un utilisateur anonyme ou précédent contamine le nouvel utilisateur.
const clearProgressLocalStorage = (preserveSentenceKeys = false) => {
  const KEYS_TO_PRESERVE = new Set([
    'hasEverUsedApp',
    'lastUsedLanguage',
    'user_subscription',
    'secureToken',
    'secureUser',
    'authToken',
    'last-progress-load',
    // Note: sync-queue, jogolinga-daily-streak et jogolinga-activity-history sont
    // intentionnellement absents : ils sont liés à l'utilisateur connecté et doivent
    // être effacés au login/logout pour éviter toute contamination entre comptes.
  ]);

  // Supprimer les clés legacy d'authentification
  ['googleToken', 'googleUser', 'tokenExpires'].forEach(k => localStorage.removeItem(k));

  // Patterns de clés de progression (indépendants de la langue)
  const isProgressKey = (key: string) =>
    key.includes('sentence-') ||
    key.includes('grammar-') ||
    key.includes('learn-') ||
    key.includes('revision-') ||
    key.includes('-conv-') ||
    key.endsWith('-progress') ||
    key.endsWith('-totalXP') ||
    key.endsWith('-completedCategories') ||
    key.endsWith('-learnedWords') ||
    key.endsWith('-reviewDueCount') ||
    key.endsWith('-daily-activity') ||
    key.endsWith('-song-last') ||
    key.includes('-temp');

  // L'historique de session est précieux et restaurable depuis Supabase uniquement si
  // la sync a eu le temps de s'exécuter. On le préserve lors du nettoyage de progression
  // pour éviter de perdre des révisions récentes en cas de changement de langue ou de
  // rechargement rapide. Il sera écrasé par loadInitialData si Supabase a des données plus récentes.
  const isSessionHistoryKey = (key: string) =>
    key.endsWith('-sessionHistory') ||
    // Heatmap "5 dernières semaines" : local uniquement, jamais restauré depuis
    // Supabase — l'effacer à chaque loadUserProgress vidait la grille à chaque
    // rechargement. Effacé explicitement au logout / changement de compte.
    key.endsWith('-daily-activity');

  // Clés à préserver pour éviter les flashs visuels au chargement
  // (elles seront immédiatement écrasées par loadProgress depuis Supabase)
  const isUserDataKey = (key: string) =>
    key.startsWith('sentence-progress-') ||
    key.startsWith('sentence-revision-') ||
    key.endsWith('-completedCategories');

  Object.keys(localStorage).forEach(key => {
    if (!KEYS_TO_PRESERVE.has(key) && isProgressKey(key) && !(preserveSentenceKeys && isUserDataKey(key)) && !isSessionHistoryKey(key)) {
      localStorage.removeItem(key);
    }
  });
};

//  NOUVEAU: Traiter la queue de sync periodiquement
const processSyncQueue = async () => {
  try {
    const queue = JSON.parse(localStorage.getItem('sync-queue') || '[]');
    
    if (queue.length === 0) return;
    

    
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token || !session?.user) {
      return;
    }
    
    const remaining = [];
    const failedItems: any[] = JSON.parse(localStorage.getItem('sync-queue-failed') || '[]');

    for (const item of queue) {
      try {
        const progress = deserializeUserProgress(item.progress);

        const success = await supabaseProgressService.saveProgress(
          item.languageCode,
          progress,
          item.categories,
          item.totalXP
        );

        if (success) {
          // Retirer de la liste des échecs si elle y était
          const idx = failedItems.findIndex((f: any) => f.languageCode === item.languageCode);
          if (idx !== -1) failedItems.splice(idx, 1);
        } else {
          item.attempts = (item.attempts || 0) + 1;
          if (item.attempts < 5) {
            remaining.push(item);
          } else {
failedItems.push({ ...item, failedAt: Date.now() });
          }
        }
      } catch (error) {
        item.attempts = (item.attempts || 0) + 1;
        if (item.attempts < 5) {
          remaining.push(item);
        } else {
          failedItems.push({ ...item, failedAt: Date.now() });
        }
      }
    }

    localStorage.setItem('sync-queue', JSON.stringify(remaining));
    localStorage.setItem('sync-queue-failed', JSON.stringify(failedItems.slice(-20)));
    
    if (remaining.length > 0) {
    
    }
  } catch (error) {
}
};


interface SaveDataContext {
  mode?: string;
  sessionStats?: any;
  description?: string;
  includeGoogleDrive?: boolean;
  grammarSpecificData?: Array<{
    subcategory: string;
    masteredWords: Array<{
      word: string;
      data: {
        translation: string;
        explanation: string;
        example: string;
        audio?: string;
      };
      correctAnswers: number;
      tested: number;
    }>;
    date: number;
    xpGained: number;
  }>;
  learnSpecificData?: {
    category: string;
    subcategory: string;
    masteredWords: Array<{
      word: string;
      data: {
        translation: string;
        explanation: string;
        example: string;
        audio?: string;
      };
      category: string;
      subcategory: string;
      timestamp: number;
    }>;
    date: number;
    totalScore: number;
    maxScore: number;
    xpGained: number;
  };
  //  NOUVEAU: Ajout pour SentenceConstructionGame
  sentenceSpecificData?: {
    category: string;
    subcategory: string;
    masteredSentences: Array<{
      original: string;
      french: string;
      audio?: string;
      mastered: boolean;
      timestamp: number;
      words: string[];
      category: string;
      attempts: number;
      firstAttemptCorrect: boolean;
    }>;
    date: number;
    totalScore: number;
    maxScore: number;
    xpGained: number;
  };
}

// Ligne user_stats lue en secours quand /verify-tier échoue : Premium
// seulement si tier/statut le disent ET que la date d'expiration (si la vue
// l'expose) n'est pas dépassée. Les webhooks de fin d'abonnement peuvent
// manquer : la ligne reste alors 'premium'/'active' après l'échéance.
const isActivePremiumRow = (row: any): boolean => {
  if (row?.tier !== 'premium' || row?.subscription_status !== 'active') return false;
  const expiresAt = row?.expires_at ?? row?.subscription_expires_at;
  return !expiresAt || new Date(expiresAt).getTime() > Date.now();
};

const App: React.FC = () => {
  const { t } = useTranslation();

  // ✅ PROTECTION : Desactiver auto-redirection sur page paiement


const [currentLanguage, setCurrentLanguage] = useState<LanguageCode | null>(null);
  // Langue courante exposée au CSS (bandeau : une porte par langue, voir TribalBorder)
  useEffect(() => {
    if (currentLanguage) document.documentElement.setAttribute('data-lang', currentLanguage);
    else document.documentElement.removeAttribute('data-lang');
  }, [currentLanguage]);
  const [showLanguageSelection, setShowLanguageSelection] = useState(false);
  const [isFirstConnection, setIsFirstConnection] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCardData, setShareCardData]   = useState<ShareCardData | null>(null);
  const [languageData, setLanguageData] = useState<LanguageData>(wolofDefaultData);
  const [mode, setMode] = useState<AppMode>('menu');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [completedCategories, setCompletedCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedWordRange, setSelectedWordRange] = useState<{ start: number; end: number } | null>(null);
  const [resetToken, setResetToken] = useState(0);
  const [userProgress, setUserProgress] = useState<UserProgress>({
  learnedWords: new Set<string>(),
  wordsToReview: new Set<string>(),
  wordProgress: {},
  recentlyLearnedWords: [],
  language: 'wf' // Valeur temporaire, sera mise e  jour
});
  const [reviewDue, setReviewDue] = useState<boolean>(false);
  // Nombre exact de mots à réviser, calculé par RevisionMode (getDueWordsForCategory).
  // Initialisé depuis localStorage pour éviter le flash 5→4 au rechargement.
  // null = jamais calculé pour cette langue → fallback sur wordsToReview.size
  const [reviewDueCount, setReviewDueCount] = useState<number | null>(() => {
    try {
      const lang = getLastUsedLanguage();
      const saved = localStorage.getItem(`${lang}-reviewDueCount`);
      return saved !== null ? parseInt(saved, 10) : null;
    } catch { return null; }
  });
  const [showCelebration, setShowCelebration] = useState<boolean>(false);
  // Catégorie qui vient d'être terminée → célébration dédiée (null = rien à afficher)
  const [categoryCelebration, setCategoryCelebration] = useState<{ category: string; askReview: boolean } | null>(null);
  // Publicités pour les utilisateurs freemium (LearnMode uniquement)

const [user, setUser] = useState<any>(null);
  const [pendingChallengesCount, setPendingChallengesCount] = useState(0);
  const pendingChallengesCountRef = useRef(0);
  useEffect(() => { pendingChallengesCountRef.current = pendingChallengesCount; }, [pendingChallengesCount]);
  // Pop-up défi : nom du dernier challenger + nombre de défis en attente
  const [challengeToast, setChallengeToast] = useState<{ name: string; count: number } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState<boolean>(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState<boolean>(false);
  const [currentStreak, setCurrentStreak] = useState<number>(0);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);

  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);

  
  
  //  AJOUT: State pour tracker l'onglet precedent
  const [lastActiveTab, setLastActiveTab] = useState<MainTab>('learn');
  
  const {
    revisionProgress,
    isLoadingProgress: isRevisionProgressLoading,
    addWordsToReview,
    refreshRevisionProgress,
    syncWithLearnedWords,
  } = useRevisionProgress(currentLanguage, user?.id ?? undefined);
  
  const [sessionLearnedWords, setSessionLearnedWords] = useState<Set<string>>(new Set());
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [navigationHistory, setNavigationHistory] = useState<AppMode[]>(['menu']);
  const [showLandingPage, setShowLandingPage] = useState(() => {
    try {
      return !localStorage.getItem('secureUser');
    } catch {
      return true;
    }
  });

  const [guestMode, setGuestMode] = useState(() => {
    try { return localStorage.getItem('guestMode') === 'true'; } catch { return false; }
  });
  
  const [showSubscriptionModal, setShowSubscriptionModal] = useState<boolean>(false);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>(SubscriptionTier.FREE);
  const [constructionFilterCategories, setConstructionFilterCategories] = useState<string[]>([]);
  const [grammarUnitFilter, setGrammarUnitFilter] = useState<number | undefined>(undefined);
  const [grammarSubcategoryFilter, setGrammarSubcategoryFilter] = useState<string[] | null>(null);
  const [blockedFeature, setBlockedFeature] = useState<string | null>(null);
  const [, setPremiumFeatureLabel] = useState<string>('');
  const [challengeTargetFriendId, setChallengeTargetFriendId] = useState<string | null>(null);
  const [initialQuizTheme, setInitialQuizTheme] = useState<import('./data/miniGamesData').QuizTheme | null>(null);
  const [showLevelUpAnim, setShowLevelUpAnim] = useState(false);
  const [levelUpInfo, setLevelUpInfo] = useState<LevelTitle | null>(null);
  const prevLevelRef = useRef<number | null>(null);
  const [showPremiumUnlockedAnim, setShowPremiumUnlockedAnim] = useState(false);
  const prevSubscriptionTierRef = useRef<SubscriptionTier | null>(null);
  const [collectibleQueue, setCollectibleQueue] = useState<CollectibleDef[]>([]);
  const trailScrollY = useRef(0);
  const pendingTrailRestore = useRef<number | null>(null);
  // Mini-jeu ouvert depuis l'onglet Pratiquer : le retour y ramène (au lieu du sentier / hub Mini-jeux)
  const openedFromPractice = useRef(false);
  // Memory Duel ouvert depuis l'écran Défier un ami : le retour y ramène
  const memoryFromChallenge = useRef(false);
  // Quiz culturel ouvert depuis Pratiquer : le hub n'affiche que les thèmes du quiz
  // (les autres jeux sont déjà sur le sentier de Pratiquer)
  const [culturalQuizOnly, setCulturalQuizOnly] = useState(false);
  const skipMenuScrollReset = useRef(false);
  const [trailActivationKey, setTrailActivationKey] = useState(0);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [streakModalCount, setStreakModalCount] = useState(0);
  const [subscriptionContext, setSubscriptionContext] = useState<'post_session' | 'streak_milestone' | 'manual'>('manual');
  const location = useLocation();
  
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const lastLoginTimeRef = useRef(0);
  // selectMode est déclaré plus bas : ref pour l'utiliser depuis des listeners montés une seule fois
  const selectModeRef = useRef<((mode: AppMode) => void) | null>(null);
  const [lastTierCheck, setLastTierCheck] = useState<number>(0);
  const [lastCheckedEmail, setLastCheckedEmail] = useState<string | null>(null);
const TIER_CHECK_COOLDOWN = 60000; // 1 minute en millisecondes

  const handleOpenSubscription = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    
    if (!token) {
setSubscriptionInfo({ tier: 'free', status: 'free' });
      setShowSubscriptionModal(true);
      return;
    }

    const userData = user || JSON.parse(localStorage.getItem('secureUser') || '{}');
    
    if (!userData.email || !userData.id) {
setShowSubscriptionModal(true);
      return;
    }

    const BACKEND_URL = process.env.REACT_APP_API_URL || 
                        'https://jogolinga-backend-production.up.railway.app';

    const response = await fetch(
      `${BACKEND_URL}/api/subscription/verify-tier`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: userData.email,
          userId: userData.id
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      setSubscriptionInfo(data);
      
    } else {
}
  } catch (error) {
}

  setTimeout(() => setShowSubscriptionModal(true), 200);
};



   const isPaymentSuccessPage = location.pathname === '/successpage' ||
                                location.pathname === '/payment-success' ||
                                location.search.includes('session_id');

  const hasLocalCredentials = (() => { try { return !!localStorage.getItem('secureUser'); } catch { return false; } })();
  

  

 
const refreshSubscriptionStatus = useCallback(async () => {
 
  
  try {
  
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
    
      setSubscriptionTier(SubscriptionTier.FREE);
      return;
    }
    
    // Verifier dans user_stats
    const { data: userStats } = await supabase
      .from('user_stats')
      .select('*')
      .eq('email', session.user.email)
      .maybeSingle();

    if (isActivePremiumRow(userStats)) {
    
      setSubscriptionTier(SubscriptionTier.PREMIUM);
      subscriptionService.updateSubscription(
        SubscriptionTier.PREMIUM, null, 'supabase_user_stats', undefined, undefined, true
      );
      setBlockedFeature(null);
      setPremiumFeatureLabel('');
      setShowSubscriptionModal(false);
    } else {
     
      setSubscriptionTier(SubscriptionTier.FREE);
    }
  } catch (error) {
   
  }
}, [setSubscriptionTier]);


// Initialisation AdMob au montage (Android uniquement)
useEffect(() => { initAdMob(); markFirstSeen(); }, []);

// Initialisation RevenueCat au montage (Android natif, si déjà connecté).
// Utilise la session Supabase (source de vérité pour le flux Google OAuth
// actif, cf. GoogleAuth.tsx), pas secureUser/secureAuthService qui ne sont
// peuplés que par l'ancien flux JWT. Rappelée aussi de façon idempotente au
// moment de l'achat dans SubscriptionModal.tsx pour couvrir le cas d'une
// connexion pendant la session en cours (pas encore de session à ce montage).
useEffect(() => {
  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        await revenueCatService.configure(session.user.id, session.user.email || null);
      }
    } catch {
      // silencieux — même politique que les autres effets d'initialisation
    }
  })();
}, []);

useEffect(() => {


  const secureUser = localStorage.getItem('secureUser');
  const cachedSubscription = localStorage.getItem('user_subscription');
  
  if (secureUser && cachedSubscription) {
    try {
      const user = JSON.parse(secureUser);
      const subscription = JSON.parse(cachedSubscription);
      
      if (user.email && subscription.email && user.email !== subscription.email) {
        
        subscriptionService.clearCache(user.email);
        setSubscriptionTier(SubscriptionTier.FREE);
      }
    } catch (error) {
}
  }
}, []);


 
useEffect(() => {
  // Traiter la queue au montage
  processSyncQueue();
  
  // Retry toutes les 60 secondes
  const intervalId = setInterval(() => {
    if (navigator.onLine) {
      processSyncQueue();
    }
  }, 60000);
  
  // Retry immediat quand on revient en ligne
  const handleOnline = () => {
   
    processSyncQueue();
  };
  
  window.addEventListener('online', handleOnline);
  
  return () => {
    clearInterval(intervalId);
    window.removeEventListener('online', handleOnline);
  };
}, []);

  
useEffect(() => {
  if (isMobileView && mode === undefined) {

    setMode('menu');
  }
}, [isMobileView, mode]);

//  STABILISATION DU RENDU MOBILE
useEffect(() => {
  if (isMobileView) {
    // S'assurer que les styles restent corrects
    if (!document.body.classList.contains('mobile-view')) {
      document.body.classList.add('mobile-view');
    }
    
    // EmpeÂªcher les conflits de styles
    const preventStyleConflicts = () => {
      document.body.style.margin = '0';
      document.body.style.padding = '60px 0 0 0';
    };
    
    preventStyleConflicts();
    
    // Verifier periodiquement que les styles sont maintenus
    const intervalId = setInterval(preventStyleConflicts, 2000);
    
    return () => clearInterval(intervalId);
  }
}, [isMobileView]);
  
  // Et appelez-la apres chaque navigation
  useEffect(() => {
    refreshSubscriptionStatus();
  }, [mode, refreshSubscriptionStatus]);



  // HOOK DE ReCUPeRATION MOBILE POST-RESET
useEffect(() => {
 
  
  if (isMobileView) {
    //  PROTECTION contre les etats undefined
    if (mode === undefined || mode === null) {
    
      setMode('menu');
      return;
    }
    
    //  MAINTIEN des styles mobile
    if (!document.body.classList.contains('mobile-view')) {
     
      // Pas de padding-top inline sur body : l'espace sous le header fixe est
      // déjà réservé par .mobile-app-container (sinon 60px en double).
      document.body.classList.add('mobile-view');
    }
  }
}, [isMobileView, mode, selectedCategory, currentLanguage]);

 



useEffect(() => {
  const handleAuthStatusChange = (event: CustomEvent) => {  // Ã¢â€ Â Sans "d"
   
    
    const { isAuthenticated } = event.detail;
    
    if (!isAuthenticated) {
  
     
      setSubscriptionTier(SubscriptionTier.FREE);
      setBlockedFeature(null);
      setPremiumFeatureLabel('');
      setShowSubscriptionModal(false);
      
      // Rafraîchir le tier depuis le service pour être sûr
      refreshSubscriptionStatus();
    }
  };

  window.addEventListener('authStatusChanged', handleAuthStatusChange as EventListener);  // Ã¢â€ Â Sans "d"
  
  return () => {
    window.removeEventListener('authStatusChanged', handleAuthStatusChange as EventListener);  // Ã¢â€ Â Sans "d"
  };
}, [refreshSubscriptionStatus]);
  const [revisionProgressState, setRevisionProgress] = useState({
    wordsToReview: new Set<string>()
  });



// Dans App.tsx, remplacez COMPLeË†TEMENT le DebugPanel par ceci :




  const handleStartRevision = useCallback((category: string) => {
    setSelectedCategory(category);
    setLastActiveTab('review');
    selectMode('menu');
  }, []);


  

  const getUniqueLearnedWordsCount = useCallback(() => {
    // Creer un Set de mots nettoyes pour eliminer les doublons
    const uniqueCleanWords = new Set<string>();
    
    // Parcourir tous les mots appris et ajouter leur version nettoyee
    Array.from(userProgress.learnedWords).forEach(word => {
      const cleanWord = cleanParentheses(word);
      uniqueCleanWords.add(cleanWord);
    });
    
    // Faire de même pour les mots appris en session
    // Exclure les marqueurs de fin de session révision (ex: "revision-1713...") qui
    // ne correspondent pas à des mots réels mais servent au déclenchement du streak.
    Array.from(sessionLearnedWords || []).forEach(word => {
      if (word.startsWith('revision-')) return;
      const cleanWord = cleanParentheses(word);
      uniqueCleanWords.add(cleanWord);
    });
    
    // Retourner le nombre de mots uniques
    return uniqueCleanWords.size;
  }, [userProgress.learnedWords, sessionLearnedWords]);

 

  const transformedWords = React.useMemo(() => {
    const transformed = transformCategories(languageData.categories);
    return transformed as Record<string, Word[]>;
  }, [languageData.categories]);

// ===================================================================
// FONCTION loadDataFromGoogleDrive COMPLeE - Pour App.tsx
// ==================================================================

  const handleUpdateRevisionState = useCallback((updatedState: { wordsToReview: Set<string> }) => {
    setRevisionProgress(updatedState || revisionProgress);
  }, [revisionProgress]);

  
const saveProgressOnSummary = useCallback(async (
  newProgress: UserProgress,
  context?: SaveDataContext
): Promise<void> => {
  
  
  try {
    //  ETAPE 1: FUSION INTELLIGENTE avec userProgress existant
   
    
    let updatedProgress: UserProgress = {
      //  Fusionner learnedWords (Set)
      learnedWords: new Set([
        ...Array.from(userProgress.learnedWords || []),
        ...Array.from(newProgress.learnedWords || [])
      ]),
      
      //  Fusionner recentlyLearnedWords (Array) - eviter doublons
      recentlyLearnedWords: (() => {
        const existing = userProgress.recentlyLearnedWords || [];
        const newWords = newProgress.recentlyLearnedWords || [];
        
        // Creer un Map pour detecter les doublons par mot
        const wordMap = new Map<string, any>();
        
        // Ajouter les existants
        existing.forEach(item => {
          if (item.word) {
            wordMap.set(item.word, item);
          }
        });
        
        // Ajouter/mettre Ã  jour avec les nouveaux
        newWords.forEach(item => {
          if (item.word) {
            // Si le mot existe dejÃ , on garde le plus recent
            const existingItem = wordMap.get(item.word);
            if (!existingItem || (item.timestamp > existingItem.timestamp)) {
              wordMap.set(item.word, item);
            }
          }
        });
        
        return Array.from(wordMap.values());
      })(),
      
      //  Fusionner wordsToReview (Set)
      wordsToReview: new Set([
        ...Array.from(userProgress.wordsToReview || []),
        ...Array.from(newProgress.wordsToReview || [])
      ]),
      
      //  Fusionner wordProgress (Object)
      wordProgress: {
        ...(userProgress.wordProgress || {}),
        ...(newProgress.wordProgress || {})
      },
      
      //  Langue
      language: currentLanguage
    };
    
    
    
    //  ETAPE 2: Gestion specialisee par mode
    if (context?.mode === 'grammar' && context?.sessionStats?.rulesLearned) {
    
      
      // Mise Ã  jour XP
      if (context.sessionStats.totalXP) {
        const currentXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
        const newTotalXP = currentXP + context.sessionStats.totalXP;
        localStorage.setItem(`${currentLanguage}-totalXP`, newTotalXP.toString());
        
        window.dispatchEvent(new CustomEvent('xpUpdated', { 
          detail: { 
            newTotal: newTotalXP, 
            gained: context.sessionStats.totalXP,
            source: 'grammar'
          } 
        }));
        
      
      }
    }
    
    if (context?.mode === 'learn' && context?.sessionStats?.wordsLearned) {
    
      
      // Mise Ã  jour XP
      if (context.sessionStats.wordsLearned > 0) {
        const xpGained = context.sessionStats.wordsLearned * 10;
        const currentXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
        const newTotalXP = currentXP + xpGained;
        localStorage.setItem(`${currentLanguage}-totalXP`, newTotalXP.toString());
        
        window.dispatchEvent(new CustomEvent('xpUpdated', { 
          detail: { 
            newTotal: newTotalXP, 
            gained: xpGained,
            source: 'learn'
          } 
        }));
        
    
      }
    }

    if (context?.mode === 'sentenceConstruction' && context?.sessionStats?.wordsLearned) {
    
      
      // Mise Ã  jour XP
      if (context.sessionStats.wordsLearned > 0) {
        const xpGained = context.sessionStats.wordsLearned * 15;
        const currentXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
        const newTotalXP = currentXP + xpGained;
        localStorage.setItem(`${currentLanguage}-totalXP`, newTotalXP.toString());
        
        window.dispatchEvent(new CustomEvent('xpUpdated', { 
          detail: { 
            newTotal: newTotalXP, 
            gained: xpGained,
            source: 'sentences'
          } 
        }));
        
       
      }
      
      // Sauvegarde specifique sentences
      if (context.sentenceSpecificData) {
        const sentenceProgressKey = `sentence-progress-${currentLanguage}`;
        const existingProgress = localStorage.getItem(sentenceProgressKey);
        const currentProgress = existingProgress ? JSON.parse(existingProgress) : [];
        
        const updatedSentenceProgress = [...currentProgress, context.sentenceSpecificData];
        localStorage.setItem(sentenceProgressKey, JSON.stringify(updatedSentenceProgress));
      
      }
    }

    const serialized = serializeUserProgress(updatedProgress);
    const storageKey = `${currentLanguage}-progress`;
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(serialized));
     
      
      // Verification immediate
      const verification = localStorage.getItem(storageKey);
      if (!verification) {
        throw new Error('Verification localStorage echouee');
      }
      
      JSON.parse(verification);
     
      
    } catch (localStorageError) {
throw new Error(`Erreur sauvegarde locale: ${localStorageError}`);
    }
   
    setUserProgress(updatedProgress);
  

    const totalXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
    
   
    // Sync Supabase immédiate avec retry progressif (500ms → 2s → 5s)
    const syncToCloud = async (attempt: number = 1): Promise<void> => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        if (!token || !session?.user) {
          addToSyncQueue(currentLanguage, updatedProgress, completedCategories || [], totalXP);
          return;
        }

        const sentenceProgressKey = `sentence-progress-${currentLanguage}`;
        const sentenceRevisionKey = `sentence-revision-${currentLanguage}-sessionHistory`;
        const progressData = localStorage.getItem(sentenceProgressKey);
        const revisionData = localStorage.getItem(sentenceRevisionKey);

        let sentenceData = undefined;
        if (progressData || revisionData) {
          sentenceData = {
            progress: progressData ? JSON.parse(progressData) : [],
            sentences: revisionData ? JSON.parse(revisionData) : [],
            metadata: {
              lastUpdated: Date.now(),
              totalSessions: progressData ? JSON.parse(progressData).length : 0,
              totalSentences: revisionData ? JSON.parse(revisionData).length : 0,
              language: currentLanguage
            }
          };
        }

        const success = await supabaseProgressService.saveProgress(
          currentLanguage,
          updatedProgress,
          completedCategories || [],
          totalXP,
          sentenceData
        );

        if (success) {
          const queue = JSON.parse(localStorage.getItem('sync-queue') || '[]');
          const filtered = queue.filter((item: any) => item.languageCode !== currentLanguage);
          localStorage.setItem('sync-queue', JSON.stringify(filtered));
        } else if (attempt < 3) {
          const delays = [500, 2000, 5000];
          setTimeout(() => syncToCloud(attempt + 1), delays[attempt - 1]);
        } else {
addToSyncQueue(currentLanguage, updatedProgress, completedCategories || [], totalXP);
        }
      } catch (cloudError) {
        if (attempt < 3) {
          const delays = [500, 2000, 5000];
          setTimeout(() => syncToCloud(attempt + 1), delays[attempt - 1]);
        } else {
addToSyncQueue(currentLanguage, updatedProgress, completedCategories || [], totalXP);
        }
      }
    };

    syncToCloud();
    
    //  ETAPE 6: Sauvegardes backup specifiques (mode-specific)
    if (context?.grammarSpecificData) {
      const grammarProgressKey = `grammar-progress-${currentLanguage}`;
      localStorage.setItem(grammarProgressKey, JSON.stringify(context.grammarSpecificData));
     
    }
    
    if (context?.learnSpecificData) {
      const learnProgressKey = `learn-sessions-${currentLanguage}`;
      const existingLearnProgress = localStorage.getItem(learnProgressKey);
      const currentLearnProgress = existingLearnProgress ? JSON.parse(existingLearnProgress) : [];
      
      const updatedLearnProgress = [...currentLearnProgress, context.learnSpecificData];
      localStorage.setItem(learnProgressKey, JSON.stringify(updatedLearnProgress));
    
    }
    
   
    
    return Promise.resolve();
    
  } catch (error) {
    //  Fallback: sauvegarder au moins le state
    try {
     
      setUserProgress(newProgress);
      
    } catch (fallbackError) {
}
    
    return Promise.resolve();
  }
}, [currentLanguage, userProgress, completedCategories, setUserProgress]);














const loadUserProgress = useCallback(async () => {
  if (!currentLanguage) {
    return;
  }
 
  
  // Réinitialiser l'état React immédiatement pour éviter d'afficher la progression
  // d'un compte précédent pendant le chargement depuis Supabase.
  setUserProgress({ learnedWords: new Set(), wordsToReview: new Set(), wordProgress: {}, recentlyLearnedWords: [], language: currentLanguage ?? 'wf' });
  setCompletedCategories([]);

  try {
    // Nettoyer localStorage uniquement si l'utilisateur est authentifié, pour éviter
    // qu'un compte précédent contamine le nouveau. Pour les invités, localStorage est
    // leur seule source de vérité : le vider provoquerait une perte de progression.
    const { data: { session: _session } } = await supabase.auth.getSession();
    if (_session?.user) {
      clearProgressLocalStorage(false);
      supabaseProgressService.clearAllCaches();
    }
    // Note: jogolinga-daily-streak et jogolinga-activity-history ne sont PAS
    // supprimés ici — ils appartiennent à l'utilisateur connecté et doivent
    // survivre aux changements de langue. La suppression inter-comptes est
    // gérée exclusivement dans handleLogout.
  } catch (cleanError) {
}

  // ===== CHARGEMENT DEPUIS SUPABASE =====
  // Charger d'abord les données journalières mutualisées (streak, baseline XP…)
  // pour qu'elles soient en localStorage avant que loadProgress réinitialise la baseline.
  await supabaseProgressService.loadDailyData(currentLanguage);

  try {
    const { userProgress, completedCategories, totalXP, sentenceData } =
      await supabaseProgressService.loadProgress(currentLanguage);

    // Si l'utilisateur n'a pas de données de phrases en Supabase,
    // s'assurer que le localStorage est vide pour cette langue.
    if (!sentenceData) {
      localStorage.removeItem(`sentence-progress-${currentLanguage}`);
      localStorage.removeItem(`sentence-revision-${currentLanguage}-sessionHistory`);
    }
    
    if (userProgress) {

      if (userProgress.recentlyLearnedWords && userProgress.recentlyLearnedWords.length > 0) {

      } else {
}

      const serializedProgress = serializeUserProgress(userProgress);
      
   
      if (serializedProgress.recentlyLearnedWords && serializedProgress.recentlyLearnedWords.length > 0) {
     
      } else {
      
      }
      
      // Sauvegarder dans localStorage
      const progressJSON = JSON.stringify(serializedProgress);
      localStorage.setItem(`${currentLanguage}-progress`, progressJSON);
      localStorage.setItem(`${currentLanguage}-completedCategories`, JSON.stringify(completedCategories));
      localStorage.setItem(`${currentLanguage}-totalXP`, totalXP.toString());

      // Reconstruire les clés par catégorie (${lang}-${cat}-learnedWords) depuis
      // recentlyLearnedWords, pour que getCategoryLearnedWords les trouve même
      // après que resetToken ait effacé ${lang}-progress.
      if (userProgress.recentlyLearnedWords && userProgress.recentlyLearnedWords.length > 0) {
        const categoryMap: Record<string, Set<string>> = {};
        userProgress.recentlyLearnedWords.forEach(item => {
          if (!item.category) return;
          if (!categoryMap[item.category]) categoryMap[item.category] = new Set();
          const clean = item.word.split('(')[0].trim();
          categoryMap[item.category].add(clean);
          categoryMap[item.category].add(item.word);
        });
        Object.entries(categoryMap).forEach(([cat, wordSet]) => {
          localStorage.setItem(
            `${currentLanguage}-${cat}-learnedWords`,
            JSON.stringify(Array.from(wordSet))
          );
        });
      }
      
      // Verification finale
      const verifRaw = localStorage.getItem(`${currentLanguage}-progress`);
      if (verifRaw) {
        const verifParsed = JSON.parse(verifRaw);
        if (!verifParsed.recentlyLearnedWords || verifParsed.recentlyLearnedWords.length === 0) {
} else {
        
        }
      } else {
}
      
      // Mise Ã  jour du state React
      setUserProgress(userProgress);
      setCompletedCategories(completedCategories);
      
      // Corriger la baseline XP journalière si elle a été initialisée avant la sync
      // (ex. nouveau navigateur : baseline écrite à 0, puis serveur charge 500 XP → faux 500 XP/jour)
      try {
        const dailyStartKey = `${currentLanguage}-daily-xp-start`;
        // Must match the local-time date CategorySelection.tsx's getDailyXP
        // compares against (SESSION_DATE) — using the UTC date here caused
        // the baseline to be wrongly treated as "stale" and reset to the
        // current totalXP (zeroing the daily goal) for any user whose local
        // date and UTC date briefly disagree.
        const todayStr = SESSION_DATE;
        const stored = localStorage.getItem(dailyStartKey);
        const parsed = stored ? JSON.parse(stored) : null;
        // `parsed.provisional` : baseline posée par getDailyXP avant la sync, à
        // partir d'un XP local périmé (ex. XP gagné hier sur le web, app Android
        // pas rouverte depuis). Sans recalage, la différence avec l'XP serveur
        // comptait comme XP du jour : objectif rempli et streak validé sans cours.
        // Le flag provisional couvre désormais le cas « nouveau navigateur » que
        // traitait l'ancienne branche `parsed.xp === 0 && totalXP > priorLocalXP`.
        // Celle-ci a été retirée : priorLocalXP était lu APRÈS que
        // clearProgressLocalStorage ait effacé ${lang}-totalXP, donc toujours 0.
        // Résultat : pour une langue commencée aujourd'hui (baseline légitime à 0),
        // chaque rechargement recalait la baseline sur l'XP gagné et remettait
        // l'objectif du jour à 0 (bug : 3 mots swahili, 30/50 → 0/50).
        if (!parsed || parsed.date !== todayStr || parsed.provisional) {
          localStorage.setItem(dailyStartKey, JSON.stringify({ date: todayStr, xp: totalXP }));
        }
      } catch { /* ignore */ }

      // Evenement XP
      window.dispatchEvent(new CustomEvent('xpUpdated', {
        detail: {
          newTotal: totalXP,
          gained: 0,
          source: 'load_progress'
        }
      }));
      
    } else {
   
      
      const newProgress: UserProgress = {
        learnedWords: new Set<string>(),
        wordsToReview: new Set<string>(),
        wordProgress: {},
        recentlyLearnedWords: [],
        language: currentLanguage
      };
      
      setUserProgress(newProgress);
      setCompletedCategories([]);
      localStorage.setItem(`${currentLanguage}-totalXP`, '0');
    }
    
  } catch (error) {
    // Gestion des erreurs
if (error instanceof Error) {
    }
    
    // Fallback : progression par defaut
    const defaultProgress: UserProgress = {
      learnedWords: new Set<string>(),
      wordsToReview: new Set<string>(),
      wordProgress: {},
      recentlyLearnedWords: [],
      language: currentLanguage
    };
    
    setUserProgress(defaultProgress);
    setCompletedCategories([]);
    
}
}, [currentLanguage, setUserProgress, setCompletedCategories]);

// ===================================================================
// FIN DE LA FONCTION loadUserProgress
// ===================================================================




















  const handleQuizAnswer = useCallback((correct: boolean) => {
    setLastAnswerCorrect(correct);
    if (correct) {
      setCurrentStreak(prev => prev + 1);
    } else {
      setCurrentStreak(0);
    }
  }, []);

  const handleWordsLearned = useCallback(async (words: LearnedWord[]) => {
   
    
    
    try {
      const cleanWords = words.map(w => ({
        ...w,
        word: w.word.split('(')[0].trim()
      }));
  
    
  
      //  NOUVEAU : Sauvegarde immediate vers la revision
      if (cleanWords.length > 0) {
       
        
        // Preparer les mots pour la revision avec le bon format
        const wordsForRevision = cleanWords.map(word => ({
          word: word.word,
          category: word.category || 'Grammaire',
          isGrammar: word.category === 'Grammaire',
          subCategory: word.subcategory || '',
          grammarType: (word as any).grammarType || 'vocabulary' as 'rule' | 'conjugation' | 'vocabulary',
          translation: word.translation,
          audio: word.audio
        }));
        
       
        
        try {
         
          await addWordsToReview(wordsForRevision);
          
          
          //  NOUVEAU : Diagnostic apreÂ¨s ajout
          setTimeout(() => {
          
           
          }, 1000);
          
        } catch (revisionError) {
         
         
        }
      }
      
      //  Mise e  jour en memoire pour l'affichage immediat
      setUserProgress(prev => ({
        ...prev,
        recentlyLearnedWords: [...prev.recentlyLearnedWords, ...cleanWords],
        learnedWords: new Set([
          ...Array.from(prev.learnedWords),
          ...cleanWords.map(w => w.word)
        ])
      }));
      
    
      
    } catch (error) {
   
    }
}, [addWordsToReview]);

  // Pub interstitielle (Android natif, non-Premium uniquement). Le cooldown
  // de 3 min dans showSessionAd() empêche le spam même si ceci est appelé
  // plusieurs fois par session (fin de leçon + fin de catégorie).
  const maybeShowSessionAd = useCallback(() => {
    const currentTier = subscriptionService.getCurrentTier();
    if (Capacitor.isNativePlatform() && currentTier !== SubscriptionTier.PREMIUM) {
      showSessionAd();
    }
  }, [subscriptionService]);

  // Avec NO_CELEBRATION : la célébration a déjà été jouée (fin de catégorie),
  // on ne garde que la pub / l'upsell. Marqueur dédié car handleSuccess est
  // passé tel quel en callback à des composants qui peuvent lui passer un argument.
  const handleSuccess = useCallback((arg?: unknown) => {
    const celebrate = arg !== NO_CELEBRATION;
    maybeShowSessionAd();

    const currentTier = subscriptionService.getCurrentTier();
    let triggerPostSessionUpsell = false;
    if (currentTier !== SubscriptionTier.PREMIUM && mode === 'learn') {
      const count = parseInt(localStorage.getItem('jogolinga-free-sessions') || '0', 10) + 1;
      localStorage.setItem('jogolinga-free-sessions', String(count));
      triggerPostSessionUpsell = count % 3 === 0;
    }

    if (!celebrate) {
      if (triggerPostSessionUpsell) {
        setSubscriptionContext('post_session');
        setShowSubscriptionModal(true);
      }
      return;
    }

    setShowCelebration(true);
    if (triggerPostSessionUpsell) {
      setSubscriptionContext('post_session');
      setTimeout(() => {
        setShowCelebration(false);
        setShowSubscriptionModal(true);
      }, 2200);
    } else {
      setTimeout(() => setShowCelebration(false), 2000);
    }
  }, [mode, subscriptionService, maybeShowSessionAd]);

  


  const handleLanguageFirstSelection = useCallback((language: LanguageCode) => {
 
  
  try {
  
    const newLanguageData = getLanguageInfo(language);
    
   
    setCurrentLanguage(language);
    saveLastUsedLanguage(language);
    markAppAsUsed();

    const alreadyDone =
      localStorage.getItem(ONBOARDING_DONE_KEY) === 'true' ||
      user?.onboardingCompleted === true;
    if (!alreadyDone) {
      setShowOnboarding(true);
    }

    setLanguageData(newLanguageData);
    
    // Initialiser la progression utilisateur
    setUserProgress({
      learnedWords: new Set<string>(),
      wordsToReview: new Set<string>(),
      wordProgress: {},
      recentlyLearnedWords: [],
      language: language
    });
    
  
    setShowLanguageSelection(false);
    setIsFirstConnection(false);
    
 
    
    
  
    setTimeout(() => {
      if (user && localStorage.getItem('googleToken')) {
      
        
      }
    }, 1000);
    
  } catch (error) {
alert(`Erreur lors du chargement de la langue ${language}. Veuillez reessayer.`);

    setShowLanguageSelection(true);
    setCurrentLanguage(null);
    setLanguageData(null);
  }
}, [user]);

 const handleLanguageChange = useCallback(async (newLanguage: LanguageCode) => {
 
  

  if (currentLanguage === newLanguage) {

    return;
  }

  // Réinitialiser immédiatement l'état des révisions pour la nouvelle langue
  setReviewDue(false);
  // Charger le compte persisté pour la nouvelle langue (null si jamais calculé)
  try {
    const saved = localStorage.getItem(`${newLanguage}-reviewDueCount`);
    setReviewDueCount(saved !== null ? parseInt(saved, 10) : null);
  } catch { setReviewDueCount(null); }

  try {
    //  1. SAUVEGARDE DE LA PROGRESSION ACTUELLE (si une langue etait deje  selectionnee)
    if (currentLanguage) {
     
      
      // Sauvegarder la progression actuelle dans localStorage
      const currentProgressKey = `${currentLanguage}-progress`;
      const currentCategoriesKey = `${currentLanguage}-completedCategories`;
      
      try {
        const serializedProgress = serializeUserProgress(userProgress);
        localStorage.setItem(currentProgressKey, JSON.stringify(serializedProgress));
        localStorage.setItem(currentCategoriesKey, JSON.stringify(completedCategories));
        
      
      } catch (saveError) {
}
    }
    
    
 
    setCurrentLanguage(newLanguage);
    saveLastUsedLanguage(newLanguage);
    
   
 
    try {
      const newLanguageData = await getLanguageDataAsync(newLanguage);
      setLanguageData(newLanguageData);

    } catch (languageDataError) {

      // Fallback vers une langue par defaut ou gestion d'erreur
      return;
    }
    
    //  4. CHARGEMENT DE LA PROGRESSION DE LA NOUVELLE LANGUE
   
    
    const newProgressKey = `${newLanguage}-progress`;
    const newCategoriesKey = `${newLanguage}-completedCategories`;
    
    let newUserProgress: UserProgress;
    let newCompletedCategories: string[] = [];
    
    try {
      // Tenter de charger la progression sauvegardee
      const savedProgress = localStorage.getItem(newProgressKey);
      const savedCategories = localStorage.getItem(newCategoriesKey);
      
      if (savedProgress) {
        const parsed = JSON.parse(savedProgress);
        newUserProgress = deserializeUserProgress(parsed);
    
      } else {
        // Creer une nouvelle progression vide
        newUserProgress = {
          learnedWords: new Set<string>(),
          wordsToReview: new Set<string>(),
          wordProgress: {},
          recentlyLearnedWords: [],
          language: newLanguage
        };
      
      }
      
      if (savedCategories) {
        newCompletedCategories = JSON.parse(savedCategories);
       
      }
    } catch (progressError) {
     
      
      // Fallback: progression vide
      newUserProgress = {
        learnedWords: new Set<string>(),
        wordsToReview: new Set<string>(),
        wordProgress: {},
        recentlyLearnedWords: [],
        language: newLanguage
      };
      newCompletedCategories = [];
    
    }
    
  
    
    setUserProgress(newUserProgress);
    setCompletedCategories(newCompletedCategories);
    
 
    
    // Revenir à l'apprentissage après changement de langue
    setMode('learn');
    setSelectedCategory(null);
    
   
    setCurrentStreak(0);
    setLastAnswerCorrect(null);
    setSessionLearnedWords(new Set());
    
    // Forcer un re-rendu avec un token de reset
    setResetToken(prev => prev + 1);
    
 
 
    
    try {
    
      if (newUserProgress.learnedWords.size > 0) {
        // Ne conserver que les mots dont on connaît la catégorie réelle.
        // Les mots sans correspondance dans recentlyLearnedWords sont déjà
        // pris en charge par recalculateWordsToReview (hook useRevisionProgress)
        // qui lit l'historique de session — les ajouter ici avec 'General' comme
        // catégorie créerait des clés fantômes (ex: wf:General:chat) en doublon.
        const wordsForRevision = Array.from(newUserProgress.learnedWords)
          .map((word: string) => {
            const recentWord = newUserProgress.recentlyLearnedWords.find(
              (recent: LearnedWord) => recent.word === word
            );
            if (!recentWord) return null;
            return {
              word: word,
              category: recentWord.category,
              isGrammar: recentWord.category === 'Grammaire',
              subCategory: recentWord.subcategory || '',
              grammarType: 'vocabulary' as const,
              translation: recentWord.translation || '',
              audio: recentWord.audio || ''
            };
          })
          .filter((w): w is NonNullable<typeof w> => w !== null);

        if (addWordsToReview && wordsForRevision.length > 0) {
          addWordsToReview(wordsForRevision);
        }
      }
    } catch (revisionError) {
    
    }
    
    //  8. CHARGEMENT DEPUIS GOOGLE DRIVE (SI CONNECTE)
    if (user && localStorage.getItem('googleToken')) {
    
      setTimeout(() => {
        try {
          
        
        } catch (driveError) {
        
        }
      }, 500); 
    } else {
     
    }
    
    // 9. NETTOYAGE MOBILE
    if (isMobileView) {

      
      // S'assurer que la classe mobile reste appliquée. Ne pas réécrire le
      // style inline de body : un padding-top !important s'ajoutait aux 60px
      // de .mobile-app-container et décalait le bandeau vers le bas.
      setTimeout(() => {
        if (!document.body.classList.contains('mobile-view')) {
          document.body.classList.add('mobile-view');
        }
      }, 100);
    }
    
   
    
   
    window.dispatchEvent(new CustomEvent('languageChanged', {
      detail: {
        oldLanguage: currentLanguage,
        newLanguage: newLanguage,
        wordsCount: newUserProgress.learnedWords.size,
        categoriesCount: newCompletedCategories.length
      }
    }));
    
  } catch (error) {
// Gestion d'erreur: essayer de revenir e  un etat stable
    try {
      setMode('menu');
      setSelectedCategory(null);
      
      // Si on ne peut pas changer de langue, au moins eviter un crash
      if (currentLanguage) {
     
      } else {
       
        // Vous pourriez vouloir definir une langue de fallback ici
      }
    } catch (fallbackError) {
    
      window.location.reload();
    }
  }
  

}, [
  currentLanguage,
  userProgress,
  completedCategories,
  setCurrentLanguage,
  setLanguageData,
  setUserProgress,
  setCompletedCategories,
  setMode,
  setSelectedCategory,
  setCurrentStreak,
  setLastAnswerCorrect,
  setSessionLearnedWords,
  setResetToken,
  addWordsToReview,
  user,

  isMobileView
]);








const handleLogin = useCallback(async (_response: any) => {

  
 
  if (isLoggingIn) {
return;
  }
  
  // Cooldown en mémoire (et non en localStorage) : il ne sert qu'à dédoublonner
  // les appels quasi simultanés d'une même page (checkSession + SIGNED_IN).
  // Persisté, il survivait à un rechargement < 5 s : handleLogin était alors
  // ignoré, `user` restait null et la bottom nav n'était jamais rendue.
  const timeSinceLastLogin = Date.now() - lastLoginTimeRef.current;

  if (timeSinceLastLogin < 5000) {

    return;
  }

  // Enregistrer le timestamp du login
  lastLoginTimeRef.current = Date.now();
  localStorage.removeItem('guestMode');
  setGuestMode(false);

  setIsLoggingIn(true);

  
  try {
   
   
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.user) {
      localStorage.removeItem('secureUser');
      setShowLandingPage(true);
      setIsLoggingIn(false);
      return;
    }
    
   
    
    
    const userData = {
      id: session.user.id,
      email: session.user.email || '',
      name: session.user.user_metadata.full_name || session.user.email || 'Utilisateur',
      picture: session.user.user_metadata.avatar_url || '',
      isAuthenticated: true
    };

    // Nettoyer les données de l'utilisateur précédent si l'email a changé ou si
    // secureUser est absent (= après un logout, où il est supprimé).
    try {
      const previousUserRaw = localStorage.getItem('secureUser');
      const previousEmail = previousUserRaw ? JSON.parse(previousUserRaw).email : null;
      if (!previousEmail || previousEmail !== userData.email) {
        clearProgressLocalStorage(false);
        supabaseProgressService.clearAllCaches();
        localStorage.removeItem('jogolinga-daily-streak');
        localStorage.removeItem('jogolinga-activity-history');
        localStorage.removeItem('jogolinga-streak-shields');
        localStorage.removeItem('sync-queue');
        localStorage.removeItem('sync-queue-failed');
        // -sessionHistory keys are intentionally spared by clearProgressLocalStorage
        // (they protect not-yet-synced reviews across language switches / quick
        // reloads for the SAME user). But here we've just confirmed the incoming
        // account differs from the previous one, so the previous user's session
        // history must not leak into the new account's due-words calculation
        // (see useRevisionProgress's recalculateWordsToReview/loadInitialData,
        // which falls back to this local history when Supabase has no data yet
        // for the new account).
        Object.keys(localStorage).forEach(key => {
          if (key.endsWith('-sessionHistory') || key.endsWith('-daily-activity')) localStorage.removeItem(key);
        });
      }
    } catch {}



    const cachedSubscription = localStorage.getItem('user_subscription');
    
    if (cachedSubscription) {
      try {
        const parsed = JSON.parse(cachedSubscription);
      
        
       
        if (parsed.email && parsed.email !== userData.email) {
       
          
          subscriptionService.clearCache(userData.email);
          setSubscriptionTier(SubscriptionTier.FREE);
          setShowSubscriptionModal(false);
          setBlockedFeature(null);
          setPremiumFeatureLabel('');
          
          
        } else {
        
        }
      } catch (cacheError) {
subscriptionService.clearCache(userData.email);
        setSubscriptionTier(SubscriptionTier.FREE);
      }
    } else {
     
    }
    
   
    setUser(userData);
   
    
   
    const now = Date.now();
    const timeSinceLastCheck = now - lastTierCheck;
    
  

   
    if (lastCheckedEmail !== userData.email) {

      setLastCheckedEmail(userData.email);
      setLastTierCheck(0); 
    }


    const cachedRaw = localStorage.getItem('user_subscription');
    let parsedCached: any = null;
    try { parsedCached = cachedRaw ? JSON.parse(cachedRaw) : null; } catch {}
    // Un Premium en cache dont la date est dépassée n'est jamais « frais » :
    // on revérifie auprès du backend.
    const cachedPremiumExpired = parsedCached?.tier === 'premium' &&
                                 !!parsedCached?.expires_at &&
                                 new Date(parsedCached.expires_at).getTime() <= now;
    const cacheIsFresh = parsedCached?.email === userData.email &&
                         parsedCached?.cachedAt &&
                         !cachedPremiumExpired &&
                         (now - parsedCached.cachedAt) < TIER_CHECK_COOLDOWN;

    // `trustedFresh` s'appuie sur un état mémoire (lastTierCheck) qui n'est
    // alimenté que par une vraie réponse backend obtenue durant CETTE session
    // — contrairement à `cacheIsFresh`, il ne peut pas être falsifié en écrivant
    // directement dans localStorage avant de recharger la page.
    const trustedFresh = timeSinceLastCheck < TIER_CHECK_COOLDOWN && lastCheckedEmail === userData.email;

    if (trustedFresh || cacheIsFresh) {
      const cached = cachedRaw;
      if (cached) {
        try {
          const parsed = parsedCached;

          //  CRITIQUE : Ne skip que si le cache appartient au BON utilisateur
          if (parsed?.email === userData.email && parsed?.tier) {
           

            const cachedTier = parsed.tier === 'premium' 
              ? SubscriptionTier.PREMIUM 
              : SubscriptionTier.FREE;

            setSubscriptionTier(cachedTier);

            //  Mettre Ã  jour explicitement le subscriptionService
            if (cachedTier === SubscriptionTier.PREMIUM) {
              const planId = parsed.plan_id || 'premium_monthly';
              const billingPeriod = parsed.billing_period || 'monthly';
              const expiresAt = parsed.expires_at ? new Date(parsed.expires_at).getTime() : null;
              
              subscriptionService.updateSubscription(
                SubscriptionTier.PREMIUM,
                expiresAt,
                parsed.verifiedBy || 'cache',
                billingPeriod,
                planId
              );
       
            } else {
              subscriptionService.updateSubscription(
                SubscriptionTier.FREE,
                null,
                'cache',
                'monthly',
                'free_plan'
              );
            }
            
            setShowLandingPage(false);
            setIsLoggingIn(false);

            // 🔒 SÉCURITÉ : un statut FREE ne présente aucun enjeu à faire
            // confiance au cache seul. Un statut PREMIUM confirmé par
            // `trustedFresh` (vraie vérification backend récente en mémoire)
            // est fiable aussi. Mais un statut PREMIUM dont la seule preuve
            // de fraîcheur est `cacheIsFresh` (basé sur un champ `cachedAt`
            // stocké dans le même localStorage que l'utilisateur peut
            // modifier) ne doit PAS être accepté sans revérification serveur
            // — sinon il suffit d'écrire un faux user_subscription premium
            // dans localStorage pour débloquer les fonctionnalités payantes.
            // On garde l'affichage optimiste ci-dessus (pas de flash "free"),
            // mais on continue plus bas pour revérifier réellement.
            if (cachedTier === SubscriptionTier.FREE || trustedFresh) {
              return;
            }
          } else {

          }
        } catch (cacheError) {
}
      }
    }
    
    
    setLastTierCheck(now);
    
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 
                         'https://jogolinga-backend-production.up.railway.app';
      
    
      
      const backendResponse = await fetch(`${backendUrl}/api/subscription/verify-tier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: userData.email,
          userId: userData.id
        })
      });
      
   
      
    
      if (!backendResponse.ok) {
        if (backendResponse.status === 429) {
const cached = localStorage.getItem('user_subscription');
          if (cached) {
            try {
              const parsed = JSON.parse(cached);
              
              if (parsed.email === userData.email && parsed.tier) {
                const cachedTier = parsed.tier === 'premium' 
                  ? SubscriptionTier.PREMIUM 
                  : SubscriptionTier.FREE;
                
                setSubscriptionTier(cachedTier);
                
                window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
                  detail: { 
                    tier: cachedTier,
                    isPremium: cachedTier === SubscriptionTier.PREMIUM
                  }
                }));
                
                setShowLandingPage(false);
                setIsLoggingIn(false);
                
        
                
                return;
              }
            } catch (parseError) {
            }
          }
          
       
          setSubscriptionTier(SubscriptionTier.FREE);
          setShowLandingPage(false);
          setIsLoggingIn(false);
          
         
          return;
        }
        
        throw new Error(`Backend error: ${backendResponse.status}`);
      }
      
      const tierData = await backendResponse.json();

      // Une date d'expiration dépassée l'emporte sur isPremium : le backend
      // peut garder l'accès (délai de grâce Play, panne Stripe) alors que
      // subscriptionService.getCurrentTier() repasse en FREE. Sans ce test, la
      // barre latérale affichait « Premium actif » sur un compte expiré.
      const backendExpiresAt = tierData.expiresAt ? new Date(tierData.expiresAt).getTime() : null;
      const premiumExpired = backendExpiresAt !== null && backendExpiresAt <= Date.now();

      if (tierData.isPremium && !premiumExpired) {
       
        
        setSubscriptionTier(SubscriptionTier.PREMIUM);
        
       localStorage.setItem('user_subscription', JSON.stringify({
  email: userData.email,
  tier: 'premium',
  status: tierData.status || 'active',
  plan_id: tierData.planId || 'premium_monthly',          // ✅ AJOUTe
  billing_period: tierData.billingPeriod || 'monthly',    // ✅ AJOUTe
  expires_at: tierData.expiresAt || null,                 // ✅ AJOUTe - LE PLUS IMPORTANT
  cancelled_at: tierData.cancelledAt || null,             // ✅ AJOUTe
  verified: true,
  verifiedBy: tierData.verifiedBy || 'backend',
  verifiedAt: tierData.verifiedAt || now,
  cachedAt: now,
  isAdmin: tierData.isAdmin || false                      // ✅ AJOUTe
}));

  subscriptionService.updateSubscription(
    SubscriptionTier.PREMIUM,
    tierData.expiresAt ? new Date(tierData.expiresAt).getTime() : null,
    tierData.verifiedBy || 'backend',
    tierData.billingPeriod || 'monthly',
    tierData.planId || 'premium_monthly',
    true // confirmé par le backend
  );
        
        window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
          detail: { 
            tier: SubscriptionTier.PREMIUM,
            isPremium: true
          }
        }));
        
      
        
      } else {
      
        
        setSubscriptionTier(SubscriptionTier.FREE);
        
        localStorage.setItem('user_subscription', JSON.stringify({
          email: userData.email,
          tier: 'free',
          status: tierData.subscription_status || 'inactive',
          verified: true,
          verifiedBy: tierData.verifiedBy || 'backend',
          cachedAt: now
        }));
        
        window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
          detail: { 
            tier: SubscriptionTier.FREE,
            isPremium: false
          }
        }));
        
    
      }
      
    } catch (backendError) {
try {
        const { data: userStats } = await supabase
          .from('user_stats')
          .select('*')
          .eq('email', userData.email)
          .maybeSingle();

        if (isActivePremiumRow(userStats)) {
       
          
          setSubscriptionTier(SubscriptionTier.PREMIUM);
          // Donnée lue en base (session Supabase) : source serveur
          subscriptionService.updateSubscription(
            SubscriptionTier.PREMIUM, null, 'supabase_fallback', undefined, undefined, true
          );
          
          localStorage.setItem('user_subscription', JSON.stringify({
            email: userData.email,
            tier: 'premium',
            status: 'active',
            verified: true,
            verifiedBy: 'supabase_fallback',
            cachedAt: now
          }));
          
          window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
            detail: { 
              tier: SubscriptionTier.PREMIUM,
              isPremium: true 
            }
          }));
          
        } else {
        
          
          setSubscriptionTier(SubscriptionTier.FREE);
          
          localStorage.setItem('user_subscription', JSON.stringify({
            email: userData.email,
            tier: 'free',
            status: 'inactive',
            verified: true,
            verifiedBy: 'supabase_fallback',
            cachedAt: now
          }));
          
          window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
            detail: { 
              tier: SubscriptionTier.FREE,
              isPremium: false
            }
          }));
        }
        
      } catch (supabaseError) {
setSubscriptionTier(SubscriptionTier.FREE);
        
        localStorage.setItem('user_subscription', JSON.stringify({
          email: userData.email,
          tier: 'free',
          status: 'error',
          verified: false,
          verifiedBy: 'error_fallback',
          cachedAt: now
        }));
        
        window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
          detail: { 
            tier: SubscriptionTier.FREE,
            isPremium: false
          }
        }));
      }
    }
   
    if (currentLanguage) {
      await loadUserProgress();
    }

    // Restaurer streak + historique d'activité depuis Supabase
    try {
      const profile = await getMyProfile(userData.id);
if (profile) {
        // 1. Fusionner activity_history EN PREMIER (nécessaire pour valider lastActivityDate)
        const profHistory: string[] = profile.activity_history || [];
        if (profHistory.length > 0) {
          const localHistRaw = localStorage.getItem('jogolinga-activity-history');
          const localHist: string[] = localHistRaw ? JSON.parse(localHistRaw) : [];
          const merged = Array.from(new Set([...localHist, ...profHistory])).sort().slice(-30);
          localStorage.setItem('jogolinga-activity-history', JSON.stringify(merged));
        }

        // 2. Recalculer le streak réel à partir de l'historique fusionné.
        // On ne fait pas confiance à profile.daily_streak qui peut être obsolète
        // (ex: streak=3 d'une semaine passée, mais activity_history n'a que 2 jours récents).
        const mergedHistRaw = localStorage.getItem('jogolinga-activity-history');
        const mergedHist: string[] = mergedHistRaw ? JSON.parse(mergedHistRaw) : [];
        // Chaîne de jours actifs avec jours de grâce (règle de useDailyStreak).
        // Perdu seulement si plus de jours manqués que la grâce ET pas assez de
        // boucliers (restaurés juste avant par loadDailyData) pour le sauver.
        const restored = computeRestoredStreak(mergedHist, profile.daily_streak || 0);
        const lastActivityDate = restored.lastActivityDate;
        const streakBroken = restored.broken;

        let restoredStreak = 0;

        if (streakBroken) {
          // Streak cassé : remettre à 0 partout
          localStorage.setItem('jogolinga-daily-streak', JSON.stringify({ date: lastActivityDate || SESSION_DATE, streak: 0 }));
          if (profile.daily_streak > 0) {
            syncStreak(userData.id, 0).catch(() => {});
          }
        } else {
          restoredStreak = restored.streak;
          localStorage.setItem('jogolinga-daily-streak', JSON.stringify({
            date: lastActivityDate,
            streak: restoredStreak
          }));
          // Corriger Supabase si le streak stocké ne correspond pas à la réalité
          if (profile.daily_streak !== restoredStreak) {
            syncStreak(userData.id, restoredStreak, mergedHist).catch(() => {});
          }
        }

        // Mettre à jour le React state de MainMenu immédiatement
        // (sans cet event, le state resterait bloqué à 0 depuis le logout)
        window.dispatchEvent(new CustomEvent('streakUpdated', {
          detail: { streak: restoredStreak, activityHistory: mergedHist }
        }));
      }
    } catch { /* silencieux */ }

    setShowLandingPage(false);
    setIsLoggingIn(false);
    

    
  } catch (error) {
    setSubscriptionTier(SubscriptionTier.FREE);
    setShowLandingPage(false);
    setIsLoggingIn(false);
    
  
  }
}, [
  currentLanguage, 
  loadUserProgress, 
  user,
  isLoggingIn, 
  lastTierCheck,
  lastCheckedEmail,
  setUser,
  setSubscriptionTier,
  setShowLandingPage,
  setShowSubscriptionModal,
  setBlockedFeature,
  setPremiumFeatureLabel
]);


// ---- Journal des gains d'XP (award_xp) ----
  // Chaque gain réel (gained > 0, activité connue) est envoyé au serveur, qui
  // le valide et met à jour l'XP cumulée toutes langues. Écouté aussi en
  // invité : le gain attend dans la file et part à la connexion.
  useEffect(() => {
    const handleAward = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (!d || !(d.gained > 0) || !XP_SOURCES.includes(d.source)) return;
      const lang = d.language || localStorage.getItem('lastUsedLanguage');
      if (lang) awardXP(lang, d.source as XpSource, d.gained);
    };
    const handleOnline = () => { flushXPQueue(); };
    window.addEventListener('xpUpdated', handleAward as EventListener);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('xpUpdated', handleAward as EventListener);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

// ---- Sync XP vers Supabase à chaque événement xpUpdated ----
  useEffect(() => {
    if (!user?.id) return;
    const handleXPSync = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.newTotal !== undefined) {
        syncXP(user.id, detail.newTotal).catch(() => {/* silencieux */});
      }
    };
    window.addEventListener('xpUpdated', handleXPSync as EventListener);
    return () => window.removeEventListener('xpUpdated', handleXPSync as EventListener);
  }, [user?.id]);

  // ---- Détection passage de niveau ----
  useEffect(() => {
    if (!currentLanguage) return;
    const initXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
    prevLevelRef.current = getLevel(initXP, currentLanguage).current.level;

    const handleXPForLevel = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || detail.source === 'load_progress') return;
      const { newTotal } = detail;
      if (newTotal === undefined) return;
      const newLevelInfo = getLevel(newTotal, currentLanguage);
      const newLevel = newLevelInfo.current.level;
      if (prevLevelRef.current !== null && newLevel > prevLevelRef.current) {
        setLevelUpInfo(newLevelInfo.current);
        setShowLevelUpAnim(true);
      }
      prevLevelRef.current = newLevel;
    };
    window.addEventListener('xpUpdated', handleXPForLevel as EventListener);
    return () => window.removeEventListener('xpUpdated', handleXPForLevel as EventListener);
  }, [currentLanguage]);

  // ---- Détection du passage à Premium (achat / restauration) ----
  useEffect(() => {
    if (prevSubscriptionTierRef.current === null) {
      // Initialise depuis le cache local pour ne pas déclencher l'animation
      // au simple chargement d'un compte déjà premium.
      try {
        const cached = JSON.parse(localStorage.getItem('user_subscription') || '{}');
        // Le cache existe sous deux formats selon qui l'a écrit en dernier :
        // { tier: 'premium', ... } (revenueCatService/paymentService) ou
        // { subscription: { tier: 'premium', ... }, ... } (subscriptionService.updateSubscription).
        const cachedTier = cached.subscription?.tier ?? cached.tier;
        prevSubscriptionTierRef.current = cachedTier === 'premium'
          ? SubscriptionTier.PREMIUM
          : SubscriptionTier.FREE;
      } catch {
        prevSubscriptionTierRef.current = SubscriptionTier.FREE;
      }
    }

    if (
      prevSubscriptionTierRef.current !== SubscriptionTier.PREMIUM &&
      subscriptionTier === SubscriptionTier.PREMIUM
    ) {
      // Le cache 'user_subscription' expire (5 min) et est purgé par plusieurs
      // routines : il ne suffit pas à distinguer un rechargement d'un achat.
      // On mémorise donc, par utilisateur, que l'écran a déjà été affiché.
      try {
        let email = 'anon';
        try {
          email = JSON.parse(localStorage.getItem('secureUser') || '{}').email || 'anon';
        } catch { /* ignore */ }
        const seenKey = `premium_welcome_seen_${email}`;
        if (localStorage.getItem(seenKey) !== '1') {
          localStorage.setItem(seenKey, '1');
          setShowPremiumUnlockedAnim(true);
        }
      } catch {
        // localStorage indisponible : on n'affiche rien plutôt que de répéter l'écran
      }
    }
    prevSubscriptionTierRef.current = subscriptionTier;
  }, [subscriptionTier]);

  // ---- Détection d'un objet de collection débloqué ----
  const collectibleCheckRef = useRef<() => void>(() => {});
  collectibleCheckRef.current = () => {
    if (!currentLanguage) return;
    const collectibles = getCollectibles(currentLanguage);
    if (collectibles.length === 0) return;

    const totalXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0', 10);
    let streak = 0;
    try { streak = JSON.parse(localStorage.getItem('jogolinga-daily-streak') || '{}').streak ?? 0; } catch { /* ignore */ }
    const availableCats = Object.keys(languageData?.categories ?? {}).filter(c => !c.includes('_'));
    const completedUnitIds = UNIT_CONFIGS
      .filter(u => {
        const validCats = availableCats.length ? u.categories.filter(c => availableCats.includes(c)) : u.categories;
        return validCats.length > 0 && validCats.every(c => completedCategories.includes(c));
      })
      .map(u => u.id);
    const wordsReviewed = Object.values(userProgress.wordProgress).filter(
      (wp: any) => wp?.nextReview != null,
    ).length;
    const unlocked = getUnlockedIds(collectibles, streak, totalXP, completedUnitIds, wordsReviewed);

    try {
      // Liste partagée avec CategorySelection et synchronisée avec Supabase
      // (saveGlobalDailyData / loadDailyData) : un objet vu sur un appareil ne
      // se rejoue ni sur un autre, ni dans l'autre popup de déblocage.
      // L'ancienne clé locale par langue y est fusionnée (migration).
      const sharedKey = 'jogolinga-shown-collectibles';
      const legacyKey = `collectibles_seen_${currentLanguage}`;
      const sharedRaw = localStorage.getItem(sharedKey);
      const legacyRaw = localStorage.getItem(legacyKey);
      const seen = new Set<string>([
        ...(sharedRaw ? JSON.parse(sharedRaw) : []),
        ...(legacyRaw ? JSON.parse(legacyRaw) : []),
      ]);
      // Première évaluation : on mémorise l'existant sans animation.
      const firstRun = sharedRaw === null && legacyRaw === null;
      const fresh = firstRun ? [] : collectibles.filter(c => unlocked.has(c.id) && !seen.has(c.id));
      // Union avec l'existant : un objet vu ne doit jamais redevenir « nouveau »,
      // même si sa condition est momentanément fausse (XP effacé puis rechargé
      // depuis Supabase, série cassée…).
      const before = seen.size;
      unlocked.forEach(id => seen.add(id));
      if (firstRun || legacyRaw !== null || seen.size !== before) {
        localStorage.setItem(sharedKey, JSON.stringify(Array.from(seen)));
        localStorage.removeItem(legacyKey);
      }
      if (fresh.length > 0) {
        setCollectibleQueue(q => [...q, ...fresh.filter(f => !q.some(x => x.id === f.id))]);
      }
    } catch { /* localStorage indisponible */ }
  };

  const collectiblesReadyRef = useRef(false);
  useEffect(() => {
    collectiblesReadyRef.current = false;
    // Laisse le temps à la progression (locale + Supabase) de se charger avant
    // de mémoriser l'existant, pour ne pas animer des objets déjà acquis.
    const timer = setTimeout(() => {
      collectiblesReadyRef.current = true;
      collectibleCheckRef.current();
    }, 5000);
    return () => clearTimeout(timer);
  }, [currentLanguage]);

  useEffect(() => {
    if (collectiblesReadyRef.current) collectibleCheckRef.current();
  }, [completedCategories, userProgress.wordProgress]);

  useEffect(() => {
    const onActivity = (e: Event) => {
      if ((e as CustomEvent).detail?.source === 'load_progress') return;
      if (!collectiblesReadyRef.current) return;
      // Laisse les autres écouteurs écrire dans localStorage avant de lire.
      setTimeout(() => collectibleCheckRef.current(), 0);
    };
    window.addEventListener('xpUpdated', onActivity);
    window.addEventListener('streakUpdated', onActivity);
    return () => {
      window.removeEventListener('xpUpdated', onActivity);
      window.removeEventListener('streakUpdated', onActivity);
    };
  }, []);

  // ---- Sync streak + historique d'activité vers Supabase ----
  useEffect(() => {
    if (!user?.id) return;
    const handleStreakSync = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.streak !== undefined) {
        syncStreak(user.id, detail.streak, detail.activityHistory).catch(() => {/* silencieux */});
      }
    };
    window.addEventListener('streakUpdated', handleStreakSync as EventListener);
    return () => window.removeEventListener('streakUpdated', handleStreakSync as EventListener);
  }, [user?.id]);


  // ── Affichage modal streak sur le récap (nouveau jour uniquement) ──
  const streakModalShownRef = useRef(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const handleStreakModal = (e: Event) => {
      if (streakModalShownRef.current) return;
      const { streak, isNewDay, silent } = (e as CustomEvent).detail ?? {};
      if (!isNewDay || !streak || silent) return;
      // Persisté pour ne pas réafficher le modal après une actualisation
      try {
        if (localStorage.getItem('jogolinga-streak-modal-shown') === SESSION_DATE) return;
        localStorage.setItem('jogolinga-streak-modal-shown', SESSION_DATE);
      } catch { /* ignore */ }
      streakModalShownRef.current = true;
      // délai pour que le modal apparaisse sur l'écran de récap
      timer = setTimeout(() => {
        setStreakModalCount(streak);
        setShowStreakModal(true);
      }, 2000);
    };
    window.addEventListener('streakUpdated', handleStreakModal as EventListener);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('streakUpdated', handleStreakModal as EventListener);
    };
  }, []);

  // ── Streak quotidien : déclenché dès le premier mot appris dans la session ──
  const streakCountedThisSession = useRef(false);
  useEffect(() => {
    if (sessionLearnedWords.size > 0 && !streakCountedThisSession.current) {
      streakCountedThisSession.current = true;
      recordActivity();
    }
    if (sessionLearnedWords.size === 0) {
      streakCountedThisSession.current = false;
    }
  }, [sessionLearnedWords]);

  // ── Streak quotidien : tout XP gagné aujourd'hui compte (grammaire, révision,
  // pratique, conversation…), pas seulement les nouveaux mots. Sans ça, l'objectif
  // du jour pouvait être atteint et la case du jour allumée avec un streak à 0.
  useEffect(() => {
    const handleXPForStreak = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Premier vrai gain d'XP : la baseline du jour devient définitive, pour
      // qu'un rechargement ultérieur ne recale pas (et n'efface pas) cet XP.
      if (detail?.source !== 'load_progress' && detail?.gained > 0) {
        try {
          const key = `${currentLanguage ?? 'wf'}-daily-xp-start`;
          const start = JSON.parse(localStorage.getItem(key) || 'null');
          if (start?.provisional) {
            localStorage.setItem(key, JSON.stringify({ date: start.date, xp: start.xp }));
          }
        } catch { /* ignore */ }
      }
      if (isStreakCountedToday()) return;
      if (detail?.source === 'load_progress') {
        // Après chargement (historique déjà fusionné) : rattraper un jour où de
        // l'XP a été gagné sans que le streak soit compté.
        try {
          const lang = currentLanguage ?? 'wf';
          const start = JSON.parse(localStorage.getItem(`${lang}-daily-xp-start`) || 'null');
          const total = parseInt(localStorage.getItem(`${lang}-totalXP`) || '0', 10);
          if (start?.date === SESSION_DATE && total - start.xp > 0) recordActivity({ silent: true });
        } catch { /* ignore */ }
        return;
      }
      // passive : XP crédité sans activité (ex. mise de défi récupérée) → pas de streak
      if (detail?.passive) return;
      if (detail?.gained === undefined || detail.gained > 0) recordActivity();
    };
    window.addEventListener('xpUpdated', handleXPForStreak);
    return () => window.removeEventListener('xpUpdated', handleXPForStreak);
  }, [currentLanguage]);

  // ── Persistance de l'XP gagné hors leçon (défis, Wordle, Memory Duel…) ──
  // Ces modes n'écrivent que ${lang}-totalXP en local. Sans sauvegarde, cet XP
  // était effacé au rechargement suivant (clearProgressLocalStorage puis valeur
  // serveur), avec l'objectif du jour. Les leçons sauvegardent déjà elles-mêmes.
  const xpSaveStateRef = useRef({ userProgress, completedCategories });
  xpSaveStateRef.current = { userProgress, completedCategories };
  useEffect(() => {
    if (!currentLanguage) return;
    const SELF_SAVING_SOURCES = new Set(['load_progress', 'learn', 'grammar', 'sentences']);
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Instantané pris au moment du gain : au changement de langue, l'état React
    // est remis à vide avant le chargement — il ne faut pas sauvegarder celui-là.
    let pending: { up: UserProgress; cc: string[]; totalXP: number } | null = null;
    const flush = () => {
      clearTimeout(timer);
      if (!pending) return;
      const { up, cc, totalXP } = pending;
      pending = null;
      supabaseProgressService.saveProgress(currentLanguage, up, cc, totalXP).catch(() => {});
    };
    const handleXPSave = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || SELF_SAVING_SOURCES.has(detail.source) || !(detail.gained > 0)) return;
      const { userProgress: up, completedCategories: cc } = xpSaveStateRef.current;
      const totalXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
      pending = { up, cc, totalXP };
      // Debounce : plusieurs crédits rapprochés (mises récupérées) → une seule sauvegarde
      clearTimeout(timer);
      timer = setTimeout(flush, 1500);
    };
    window.addEventListener('xpUpdated', handleXPSave);
    return () => {
      window.removeEventListener('xpUpdated', handleXPSave);
      flush(); // changement de langue : ne pas perdre un gain en attente
    };
  }, [currentLanguage]);

  // ── Notifications : défis reçus en temps réel ──
  useEffect(() => {
    if (!user?.id) {
      setPendingChallengesCount(0);
      return;
    }
    // Charge le compteur initial et affiche le pop-up s'il reste des défis
    // en attente (une fois par session, pour ne pas le répéter à chaque reconnexion)
    getPendingChallenges(user.id)
      .then(list => {
        setPendingChallengesCount(list.length);
        if (list.length === 0) return;
        const key = `challengeToastShown:${user.id}`;
        try {
          if (sessionStorage.getItem(key)) return;
          sessionStorage.setItem(key, '1');
        } catch (_) {}
        setChallengeToast({ name: list[0].challenger_username || "Quelqu'un", count: list.length });
      })
      .catch(() => {});

    // Écoute les nouveaux défis en temps réel
    const channel = supabase
      .channel('challenges-realtime')
      .on(
        'postgres_changes' as any,
        { event: 'INSERT', schema: 'public', table: 'challenges', filter: `challenged_id=eq.${user.id}` },
        async (payload: any) => {
          const count = pendingChallengesCountRef.current + 1;
          pendingChallengesCountRef.current = count;
          setPendingChallengesCount(count);
          // Récupère le pseudo du challenger pour la notification
          let name = 'Quelqu\'un';
          try {
            const { data } = await supabase
              .from('user_profiles')
              .select('display_name, username')
              .eq('id', payload.new.challenger_id)
              .single();
            if (data) name = data.display_name || data.username || name;
          } catch (_) {}
          setChallengeToast({ name, count });
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // ── Android : notifications push de défi (FCM) ──
  // Enregistre l'appareil dès qu'un utilisateur est connecté (demande la
  // permission une seule fois). Sans effet hors Android natif.
  useEffect(() => {
    if (!user?.id) return;
    enableChallengePush();
    syncWebPush();
    syncOnboardingToAccount(user.id);
    flushXPQueue();
  }, [user?.id]);

  // Tap sur la notification système d'un défi → ouvrir l'Arène
  useEffect(() => {
    const onChallengeTap = () => {
      setChallengeToast(null);
      openedFromPractice.current = true; // le retour ramène à l'onglet Pratiquer
      selectModeRef.current?.('challenge');
    };
    window.addEventListener(CHALLENGE_TAP_EVENT, onChallengeTap);
    return () => window.removeEventListener(CHALLENGE_TAP_EVENT, onChallengeTap);
  }, []);

  // Auto-fermeture du toast après 7s
  useEffect(() => {
    if (!challengeToast) return;
    const t = setTimeout(() => setChallengeToast(null), 7000);
    return () => clearTimeout(t);
  }, [challengeToast]);

const handleLogout = useCallback(async () => {

  try {

    const currentUserEmail = user?.email || null;
  
    

    
  
    if (currentLanguage && userProgress) {
   
      
      try {
        const totalXP = parseInt(
          localStorage.getItem(`${currentLanguage}-totalXP`) || '0'
        );
       
        
        const success = await supabaseProgressService.saveProgress(
          currentLanguage,
          userProgress,
          completedCategories,
          totalXP
        );
        
        if (success) {
        
        } else {
}
      } catch (saveError) {
      }
    } else {
  
    }
    
   

    
    if (currentUserEmail) {
     
    }
    
    //  Afficher le cache avant nettoyage (debug)
    const cacheBeforeCleanup = localStorage.getItem('user_subscription');
    if (cacheBeforeCleanup) {
      try {
        JSON.parse(cacheBeforeCleanup);
      
      } catch (e) {
}
    }
    
    
    subscriptionService.clearCache();
    supabaseProgressService.clearAllCaches();



    setSubscriptionTier(SubscriptionTier.FREE);
    setShowSubscriptionModal(false);
    setBlockedFeature(null);
    setPremiumFeatureLabel('');
    
  
  
    const cacheAfterCleanup = localStorage.getItem('user_subscription');
    if (!cacheAfterCleanup) {
  
    } else {
     
     
      localStorage.removeItem('user_subscription');

    }
    
   
    window.dispatchEvent(new CustomEvent('subscriptionUpdated', {
      detail: { 
        tier: SubscriptionTier.FREE,
        isPremium: false
      }
    }));
    
  
    

    
    // Supprimer toutes les données de progression du localStorage
    // pour qu'un autre utilisateur qui se connecterait sur cet appareil
    // ne voie pas les données de cet utilisateur.
    clearProgressLocalStorage();
    // Streak et historique : suppression explicite car leurs clés ne matchent
    // pas isProgressKey() et ne seraient pas effacées autrement.
    localStorage.removeItem('jogolinga-daily-streak');
    localStorage.removeItem('jogolinga-activity-history');
    // Boucliers : propres au compte, comme le streak (sinon le compte suivant en hérite)
    localStorage.removeItem('jogolinga-streak-shields');
    // Heatmap : épargnée par clearProgressLocalStorage, donc effacée ici
    Object.keys(localStorage).forEach(key => {
      if (key.endsWith('-daily-activity')) localStorage.removeItem(key);
    });
    // Dispatcher un event pour que MainMenu remette le badge à 0 immédiatement.
    window.dispatchEvent(new CustomEvent('streakUpdated', { detail: { streak: 0, activityHistory: [] } }));

    // Détacher le token FCM de ce compte avant de perdre la session
    // Gains en attente envoyés tant que la session existe (sinon ils
    // seraient crédités au prochain compte connecté sur l'appareil)
    try { await flushXPQueue(); } catch {}
    try { await unregisterPushOnLogout(); } catch {}
    clearCachedLifetimeXP();

    const { error: signOutError } = await supabase.auth.signOut();
    
    if (signOutError) {
      
    } else {
    
    }
    
   
  
    
    // Reset le state utilisateur et la progression
    setUser(null);
    setUserProgress({ learnedWords: new Set(), wordsToReview: new Set(), wordProgress: {}, recentlyLearnedWords: [], language: currentLanguage ?? 'wf' });
    setCompletedCategories([]);
    // Vider la sync-queue : elle appartient à l'utilisateur déconnecté et ne doit pas
    // être traitée ni transmise à un autre compte sur ce navigateur.
    localStorage.removeItem('sync-queue');
    localStorage.removeItem('sync-queue-failed');
   
    
    // Nettoyer tous les tokens
    const tokensToRemove = [
      'secureToken',
      'secureUser',
      'googleToken',
      'tokenExpires',
      'authToken',
      'user_subscription'  // Double securite
    ];
    
    tokensToRemove.forEach(key => {
      const existed = localStorage.getItem(key) !== null;
      localStorage.removeItem(key);
      if (existed) {
      
      }
    });
    
   
    
   
 
    
    // Retour au menu principal, onglet Apprendre. Sans ce reset, l'onglet reste
    // 'profile' (déconnexion depuis la page Profil) et, à la reconnexion, le menu
    // affichait l'ancien onglet profil de MainMenu au lieu de la page Profil.
    setLastActiveTab('learn');
    setMode('menu');
    setSelectedCategory(null);
    setShowLandingPage(true);
    
    // Reset des etats de session
    setCurrentStreak(0);
    setLastAnswerCorrect(null);
    setSessionLearnedWords(new Set());
    
  
    
  
    
    window.dispatchEvent(new Event('sessionExpired'));
  
    
    // Notifier changement d'etat d'authentification
    window.dispatchEvent(new CustomEvent('authStatusChanged', {
      detail: { 
        isAuthenticated: false, 
        user: null 
      }
    }));
  
    
    const finalChecks = {
      user: user === null,
      cache: localStorage.getItem('user_subscription') === null,
      tier: subscriptionService.getCurrentTier() === SubscriptionTier.FREE,
      showModal: showSubscriptionModal === false
    };
    
 
    
    const allChecksPass = Object.values(finalChecks).every(check => check === true);
    
    if (allChecksPass) {
   
    } else {
}
    
 
    
  } catch (error) {
   
    
if (error instanceof Error) {
    }
    
  
   
    
    try {
      // Force le nettoyage
      setUser(null);
      subscriptionService.clearCache();
      setSubscriptionTier(SubscriptionTier.FREE);
      setShowSubscriptionModal(false);
      setBlockedFeature(null);
      setPremiumFeatureLabel('');
      
      // Nettoyage manuel du localStorage
      localStorage.removeItem('user_subscription');
      localStorage.removeItem('secureToken');
      localStorage.removeItem('secureUser');
      localStorage.removeItem('googleToken');
      
     
      window.dispatchEvent(new Event('sessionExpired'));
      window.dispatchEvent(new CustomEvent('authStatusChanged', {
        detail: { isAuthenticated: false, user: null }
      }));
      
      // Retour Ã  la landing page
      setShowLandingPage(true);
      
 
      
    } catch (forceError) {
      
      // En dernier recours, recharger la page
      if (window.confirm('Une erreur critique est survenue. Recharger la page ?')) {
        window.location.reload();
      }
    }
  }
}, [
  currentLanguage, 
  userProgress, 
  completedCategories, 
  user,
  setUser,
  setSubscriptionTier,
  setShowSubscriptionModal,
  setBlockedFeature,
  setPremiumFeatureLabel,
  setMode,
  setSelectedCategory,
  setShowLandingPage,
  setCurrentStreak,
  setLastAnswerCorrect,
  setSessionLearnedWords,
  showSubscriptionModal
]);
  

  const selectCategory = useCallback((category: string, wordRange?: { start: number; end: number }): void => {
    setSelectedWordRange(wordRange ?? null);
    setSelectedCategory(category);
    setMode('learn');
  }, []);

  const handleLearnCategorySelect = useCallback((category: string, wordRange?: { start: number; end: number }): void => {
    selectCategory(category, wordRange);
  }, [selectCategory]);

  // Fin de l'onboarding : ouvre directement la 1re leçon de l'unité 1 au lieu
  // de laisser le nouvel utilisateur seul devant le sentier.
  // Kama a son propre parcours de leçon (KamaLearnMode) : on le laisse tel quel.
  const handleStartFirstLesson = useCallback(() => {
    if (!currentLanguage || currentLanguage === 'ka') return;
    const cats = languageData?.categories as Record<string, Record<string, WordData>> | undefined;
    if (!cats) return;
    for (const cat of UNIT_CONFIGS[0].categories) {
      if (!cats[cat]) continue;
      const wordCount = Object.keys(cats[cat])
        .filter(k => !k.includes('_') && !cats[cat][k]?.isSectionHeader).length;
      if (wordCount === 0) continue;
      setLastActiveTab('learn');
      handleLearnCategorySelect(
        cat,
        // même découpage que le sentier
        wordCount > TRAIL_SPLIT_SIZE ? splitIntoParts(wordCount)[0] : undefined
      );
      return;
    }
  }, [currentLanguage, languageData, handleLearnCategorySelect]);

  const handleBackFromLearn = useCallback(() => {
    
    
    //  CORRECTION : Ne pas changer selectedCategory si on est en mode summary
    if (mode === 'learn' && selectedCategory) {
    
      const isInSummaryProcess = localStorage.getItem(`${currentLanguage}-${selectedCategory}-summary-in-progress`);
      
      if (!isInSummaryProcess) {
        setSelectedCategory(null);
        setSelectedWordRange(null);
      }
    }
  }, [mode, selectedCategory, currentLanguage]);

  // ── Enchaînement « Suivant » : suit exactement l'ordre du sentier ─────────
  const trailSequence = useMemo(
    () => buildTrailSequence(
      currentLanguage ?? languageData.code,
      languageData.categories as Record<string, any>,
      languageData.sentencesToConstruct as Array<{ category?: string; original?: string }> | undefined
    ),
    [currentLanguage, languageData]
  );

  // Étape gratuite où aller si l'utilisateur ferme l'offre Premium proposée
  // par l'enchaînement (undefined = aucune redirection en attente).
  const afterPremiumOffer = useRef<TrailNavStep | null | undefined>(undefined);

  const openTrailStep = useCallback((step: TrailNavStep | null, fromIndex: number) => {
    if (!step) {
      setConversationId(null);
      setSelectedCategory(null);
      setSelectedWordRange(null);
      setLastActiveTab('learn');
      setMode('menu');
      return;
    }
    if (step.kind === 'vocab') {
      setConversationId(null);
      handleLearnCategorySelect(step.category, step.range);
      return;
    }
    if (step.kind === 'conversation') {
      setConversationId(step.conversationId);
      setMode('dialogue');
      return;
    }
    if (step.kind === 'grammar') {
      setGrammarUnitFilter(step.unitId);
      setGrammarSubcategoryFilter(step.subcats);
    } else {
      setConstructionFilterCategories(step.cats);
    }
    if (subscriptionService.getCurrentTier() !== SubscriptionTier.PREMIUM) {
      // Offre Premium ; si elle est fermée → prochaine étape gratuite
      const stepIndex = trailSequence.indexOf(step);
      afterPremiumOffer.current = nextTrailStep(trailSequence, stepIndex >= 0 ? stepIndex : fromIndex, true);
      setBlockedFeature(step.kind === 'grammar' ? 'grammar_full' : 'sentence_construction');
      setShowSubscriptionModal(true);
      return;
    }
    setMode(step.kind === 'grammar' ? 'grammar' : 'sentenceConstruction');
  }, [trailSequence, handleLearnCategorySelect, setConversationId, setMode,
      setGrammarUnitFilter, setGrammarSubcategoryFilter, setConstructionFilterCategories,
      setBlockedFeature, setShowSubscriptionModal]);

  const goToNextTrailStep = useCallback((current: Parameters<typeof findTrailStepIndex>[1]) => {
    const idx = findTrailStepIndex(trailSequence, current);
    openTrailStep(idx >= 0 ? nextTrailStep(trailSequence, idx) : null, idx);
  }, [trailSequence, openTrailStep]);

  const handleSubscriptionModalClose = useCallback(() => {
    setShowSubscriptionModal(false);
    setSubscriptionContext('manual');
    const pending = afterPremiumOffer.current;
    afterPremiumOffer.current = undefined;
    if (pending !== undefined) {
      setBlockedFeature(null);
      openTrailStep(pending, -1);
    }
  }, [openTrailStep]);

  const handleNextCategory = useCallback(() => {
    if (!selectedCategory) return;
    goToNextTrailStep({ kind: 'vocab', category: selectedCategory, range: selectedWordRange });
  }, [selectedCategory, selectedWordRange, goToNextTrailStep]);

 const handleSubscriptionSuccess = useCallback(async () => {
  // Achat réussi : on reste sur l'étape Premium, pas de redirection à la fermeture
  afterPremiumOffer.current = undefined;
 
  
  // ✅ ÉTAPE 1: Recharger les données d'abonnement depuis le backend
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    
    if (token) {
      const userData = user || JSON.parse(localStorage.getItem('secureUser') || '{}');
      
      if (userData.email && userData.id) {
        const BACKEND_URL = process.env.REACT_APP_API_URL || 
                            'https://jogolinga-backend-production.up.railway.app';

    
        
        // Appel API pour récupérer les données mises à jour
        const response = await fetch(
          `${BACKEND_URL}/api/subscription/verify-tier`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              email: userData.email,
              userId: userData.id
            })
          }
        );

        if (response.ok) {
          const data = await response.json();
     
          
        
          setSubscriptionInfo(data);
          
          // ✅ Attendre un peu que React propage les changements
          await new Promise(resolve => setTimeout(resolve, 200));
          
        
          
        } else {
await response.text();
}
      } else {
}
    } else {
}
  } catch (error) {
if (error instanceof Error) {
    }
  }
  
  // ✅ ÉTAPE 2: Forcer le refresh du tier depuis subscriptionService
  const tier = subscriptionService.getCurrentTier();
 
  
  setSubscriptionTier(tier);
  
  // ✅ ÉTAPE 3: Réinitialiser les états de blocage
  setBlockedFeature(null);
  setPremiumFeatureLabel('');
  
  
  
  // ✅ ÉTAPE 4: Redirection vers la fonctionnalité demandée (si applicable)
  if (blockedFeature) {
  
    
    // Redirection vers la fonctionnalité demandée
    if (blockedFeature === 'categories_full' && selectedCategory) {
      setMode('learn');
    } else if (blockedFeature === 'revision_basic' || blockedFeature === 'revision_unlimited') {
      setMode('review');
    } else if (blockedFeature === 'grammar_full') {
      setMode('grammar');
    } else if (blockedFeature === 'sentence_construction') {
      setMode('sentenceConstruction');
    } else if (blockedFeature === 'sentence_gap') {
      setMode('sentenceGap');
    } else if (blockedFeature === 'forge_game') {
      setMode('forge');
    } else if (blockedFeature === 'weaver_game') {
      setMode('weaver');
    } else if (blockedFeature === 'exercise_basic' || blockedFeature === 'exercise_mode') {
      setMode('exercise');
    } else if (blockedFeature === 'progress_stats_basic' || blockedFeature === 'progress_stats') {
      setMode('progression');
    }
  }
  
  
}, [
  blockedFeature, 
  selectedCategory, 
  user, 
  setSubscriptionInfo,      // ✅ IMPORTANT: Ajouter cette dépendance
  setSubscriptionTier, 
  setBlockedFeature, 
  setPremiumFeatureLabel, 
  setMode
]);


  useEffect(() => {
  const handleAuthStatusChange = (event: CustomEvent) => {
  
    
    const { isAuthenticated, user } = event.detail;
    
    if (!isAuthenticated && !user) {
      // Deconnexion detectee - Reinitialiser le tier Ã   FREE
    
      setSubscriptionTier(SubscriptionTier.FREE);
      setShowSubscriptionModal(false);
      setBlockedFeature(null);
      setPremiumFeatureLabel('');
      
      // Réinitialiser uniquement le tier et le modal -- handleLogout a déjà
      // tout nettoyé avant de dispatcher cet event, le rappeler crée une boucle.
      localStorage.removeItem('user_subscription');
    }
  };


  window.addEventListener('authStatusChanged', handleAuthStatusChange as EventListener);
  
  return () => {
    window.removeEventListener('authStatusChanged', handleAuthStatusChange as EventListener);
  };
}, [handleLogout]);


  const handleNavigateToPrivacy = useCallback(() => {

  // Si vous utilisez React Router
  // navigate('/privacy');
  
  // Navigation directe (fallback)
  window.location.href = '/privacy';
}, []);

const handleNavigateToTerms = useCallback(() => {

  // Si vous utilisez React Router
  // navigate('/terms');
  
  // Navigation directe (fallback)  
  window.location.href = '/terms';
}, []);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

 const confirmResetProgress = useCallback(async () => {
  try {
    setIsLoading(true);
  
    
    await resetProgress({
      setCompletedCategories,
      setUserProgress,
      setQuizScore: () => {},
      user,
      saveDataToGoogleDrive: async () => {
        //  NE RIEN FAIRE - Les fichiers ont deje  ete supprimes par resetProgress
      
      },
      currentLanguage
    });
      
      // Fermer la boite de dialogue de confirmation
      setShowResetConfirmation(false);
      
      // La fonction resetProgress s'occupe de recharger la page
    } catch (error) {
     
      setIsLoading(false);
      setShowResetConfirmation(false);
      
      
      alert("Une erreur est survenue lors de la reinitialisation. Veuillez reessayer.");
    }
  }, [currentLanguage, user, setCompletedCategories, setUserProgress, setShowResetConfirmation]);


  const updateWordProgress = useCallback((_category: string, word: string, correct: boolean) => {
    handleQuizAnswer(correct);
    
    setUserProgress(prev => {
      const updatedProgress = {
        ...prev,
        wordProgress: {
          ...prev.wordProgress,
          [word]: {
            ...prev.wordProgress[word],
            correct: correct ? (prev.wordProgress[word]?.correct || 0) + 1 : prev.wordProgress[word]?.correct || 0,
            incorrect: !correct ? (prev.wordProgress[word]?.incorrect || 0) + 1 : prev.wordProgress[word]?.incorrect || 0,
            tested: (prev.wordProgress[word]?.tested || 0) + 1,
            lastReviewed: Date.now(),
            interval: correct ? (prev.wordProgress[word]?.interval || 1) * 2 : 1,
            language: currentLanguage
          }
        }
      };
  

      const progressForWord = updatedProgress.wordProgress[word];
      if (progressForWord.correct >= CORRECT_ANSWERS_TO_LEARN) {
        updatedProgress.learnedWords = new Set([...prev.learnedWords, word]);
        // Revision queue is managed by addWordsToReview (useRevisionProgress)
      }
  
    
      
      return updatedProgress;
    });
  }, [currentLanguage, handleQuizAnswer]); //  Suppression de saveProgress des dependances

  const handleCategoryComplete = useCallback((category: string) => {
    const newCategories = [...completedCategories];
    if (!newCategories.includes(category)) {
      newCategories.push(category);
      localStorage.setItem(`${currentLanguage}-completedCategories`, JSON.stringify(newCategories));
      setCompletedCategories(newCategories);

      // Sync immédiat vers Supabase pour ne pas perdre la complétion si l'utilisateur quitte
      const totalXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
      supabaseProgressService.saveProgress(
        currentLanguage,
        userProgress,
        newCategories,
        totalXP
      ).catch(() => {
        addToSyncQueue(currentLanguage, userProgress, newCategories, totalXP);
      });

      // Nouvelle catégorie terminée → célébration dédiée. La suite (demande
      // d'avis Google Play, ou pub / upsell) attend sa fermeture, pour ne
      // pas empiler les fenêtres — voir handleCategoryCelebrationClose.
      setCategoryCelebration({ category, askReview: shouldAskForReview(newCategories.length) });
      return;
    }
    handleSuccess();
  }, [currentLanguage, completedCategories, handleSuccess, userProgress]);

  const categoryCelebrationRef = useRef(categoryCelebration);
  categoryCelebrationRef.current = categoryCelebration;
  const handleCategoryCelebrationClose = useCallback(() => {
    const current = categoryCelebrationRef.current;
    if (!current) return; // déjà fermée (bouton + minuteur)
    categoryCelebrationRef.current = null;
    setCategoryCelebration(null);
    if (current.askReview) requestAppReview();
    else handleSuccess(NO_CELEBRATION);
  }, [handleSuccess]);

  // Kama's lesson flow (KamaCategoryPreview → KamaLearnMode) never mounts
  // the generic LearnMode component, so it can't rely on the
  // onWordLearned/onWordsLearned/saveProgressOnSummary props App.tsx wires
  // into <LearnMode> below — this mirrors that same session-end sequence
  // (daily-activity heatmap, spaced-repetition queue, sessionLearnedWords
  // for the streak, XP via saveProgressOnSummary, completedCategories) for
  // the one call KamaLearnMode makes when a category's book is read through.
  // "Lecture analytique" and "Phrase" are pure grammar/methodology — no
  // entry in them is a real vocabulary word. Within "Lire les hiéroglyphes"
  // (mostly the real 24-letter alphabet + pr/nfr/sn), these specific cards
  // are narrative/methodology too. Excluded from the spaced-repetition
  // queue and from userProgress.learnedWords so RevisionMode never quizzes
  // "Traduisez en français : zẖꜣw : un exemple complet" — XP/streak/
  // completedCategories below are unaffected, they still count the full
  // lesson regardless of this filter.
  // Shared with CategorySelection.tsx so progress bars/chunk totals agree
  // with what actually gets recorded as "learned" here — see nonVocab.ts.
  const KAMA_NON_VOCAB_CATEGORIES = KAMA_NON_VOCAB_CATEGORIES_SHARED;
  const KAMA_NON_VOCAB_KEYS_IN_LECTURE = KAMA_NON_VOCAB_KEYS_IN_LECTURE_SHARED;

  // Categories that never get the post-browse test at all — on top of the
  // XP-level KAMA_NON_VOCAB_CATEGORIES (Lecture analytique/Phrase, which
  // have no real vocabulary anyway), "Lire les hiéroglyphes" is explicitly
  // excluded from the test too: it stays exactly as before (full credit as
  // soon as the alphabet book is browsed through), it just never offers
  // "Commencer le test" like LearnMode's other categories. This is separate
  // from KAMA_NON_VOCAB_CATEGORIES on purpose — the alphabet still counts as
  // real vocabulary for XP/spaced-repetition purposes, it's only excluded
  // from the *test* mechanism.
  const KAMA_TEST_EXCLUDED_CATEGORIES = React.useMemo(
    () => new Set([...Array.from(KAMA_NON_VOCAB_CATEGORIES), 'Lire les hiéroglyphes']),
    [KAMA_NON_VOCAB_CATEGORIES],
  );

  // Per-word predicate for KamaLearnMode's post-browse test.
  const isKamaWordTestable = useCallback((category: string, _key: string) =>
    !KAMA_TEST_EXCLUDED_CATEGORIES.has(category),
    [KAMA_TEST_EXCLUDED_CATEGORIES]);

  const handleKamaLessonComplete = useCallback((category: string, words: LearnedWord[], isFinalSegment: boolean = true) => {
    if (words.length === 0) return;

    try {
      const _d = new Date();
      const todayKey = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;
      const raw = localStorage.getItem(`${currentLanguage}-daily-activity`);
      const map: Record<string, number> = raw ? JSON.parse(raw) : {};
      map[todayKey] = (map[todayKey] ?? 0) + words.length;
      localStorage.setItem(`${currentLanguage}-daily-activity`, JSON.stringify(map));
    } catch {}

    const reviewableWords = KAMA_NON_VOCAB_CATEGORIES.has(category)
      ? []
      : words.filter(w => !(category === 'Lire les hiéroglyphes' && KAMA_NON_VOCAB_KEYS_IN_LECTURE.has(w.word)));

    if (reviewableWords.length > 0) {
      handleWordsLearned(reviewableWords);
    }
    setSessionLearnedWords(prev => new Set([...prev, ...words.map(w => w.word)]));

    // Kama never mounts LearnMode, so it misses the onLessonSummary ad the
    // other languages get at the end of every lesson — without this, a part
    // that doesn't finish its category ("Famille · 1") never shows one.
    // showSessionAd()'s 3-min cooldown absorbs the handleSuccess call below
    // when this same part also completes the category.
    if (isFinalSegment) {
      maybeShowSessionAd();
    }

    const finalProgress: UserProgress = {
      ...userProgress,
      learnedWords: new Set([
        ...Array.from(userProgress.learnedWords),
        ...reviewableWords.map(w => w.word),
      ]),
      recentlyLearnedWords: [
        ...userProgress.recentlyLearnedWords,
        ...reviewableWords,
      ],
    };

    saveProgressOnSummary(finalProgress, {
      mode: 'learn',
      sessionStats: {
        wordsLearned: words.length,
        totalScore: words.length,
        maxScore: words.length,
        accuracy: 100,
      },
      description: `Fin de session Kama - ${category} - ${words.length} mots parcourus`,
      includeGoogleDrive: true,
    });

    // Same check as the generic language flow (LearnMode.tsx's own
    // handleGameComplete): compare the FULL, un-sliced category against
    // everything learned so far, rather than trusting isFinalSegment alone.
    // KamaLearnMode only ever sees one SPLIT_SIZE-cut slice of a category
    // (see CategorySelection.tsx's split loop — e.g. "Famille" opens as
    // "Famille · 1"/"Famille · 2"), so isFinalSegment there only means "last
    // segment of THAT slice" — trusting it directly used to mark the whole
    // category "completed" (sync + celebration/ad) after just the first
    // part. Narrative/methodology categories (KAMA_NON_VOCAB_CATEGORIES)
    // are short and never split, so isFinalSegment alone is still exactly
    // right for them — same as before.
    if (KAMA_NON_VOCAB_CATEGORIES.has(category)) {
      if (isFinalSegment) {
        handleCategoryComplete(category);
      }
    } else {
      // Mirror reviewableWords' own exclusions exactly, or this count would
      // include cards (section headers; the alphabet-letter cards excluded
      // from "Lire les hiéroglyphes" via KAMA_NON_VOCAB_KEYS_IN_LECTURE)
      // that can never appear in recentlyLearnedWords — which would make
      // fullCategoryWordKeys.every(...) below permanently false for that
      // category.
      const fullCategoryData = languageData.categories[category] ?? {};
      const fullCategoryWordKeys = Object.keys(fullCategoryData)
        .filter(k => !k.includes('_'))
        .filter(k => !(fullCategoryData as Record<string, any>)[k]?.isSectionHeader)
        .filter(k => !(category === 'Lire les hiéroglyphes' && KAMA_NON_VOCAB_KEYS_IN_LECTURE.has(k)));
      const learnedForCategory = new Set(
        finalProgress.recentlyLearnedWords.filter(w => w.category === category).map(w => w.word)
      );
      if (fullCategoryWordKeys.length > 0 && fullCategoryWordKeys.every(k => learnedForCategory.has(k))) {
        handleCategoryComplete(category);
      }
    }
  }, [currentLanguage, userProgress, handleWordsLearned, saveProgressOnSummary, handleCategoryComplete, KAMA_NON_VOCAB_CATEGORIES, KAMA_NON_VOCAB_KEYS_IN_LECTURE, languageData, maybeShowSessionAd]);

  const handleConversationComplete = useCallback(() => {
    // Interstitiel AdMob après conversation (Android natif, non-Premium uniquement)
    if (Capacitor.isNativePlatform() && subscriptionService.getCurrentTier() !== SubscriptionTier.PREMIUM) {
      showSessionAd();
    }

    const totalXP = parseInt(localStorage.getItem(`${currentLanguage}-totalXP`) || '0');
    supabaseProgressService.saveProgress(
      currentLanguage,
      userProgress,
      completedCategories,
      totalXP
    ).catch(() => {});

    // Étape suivante du sentier après cette conversation
    if (conversationId) goToNextTrailStep({ kind: 'conversation', conversationId });
    else { setConversationId(null); setMode('menu'); }
  }, [currentLanguage, completedCategories, userProgress, conversationId,
      goToNextTrailStep, setConversationId]);

  // ── Navigation après une partie de grammaire complétée ──────────────────────
  const navigateAfterGrammar = useCallback((unitId: number, currentSubcats: string[] | null) => {
    goToNextTrailStep({ kind: 'grammar', unitId, subcats: currentSubcats });
  }, [goToNextTrailStep]);

  // ── Navigation après une session de phrases complétée ────────────────────────
  const navigateAfterPhrases = useCallback((currentCats: string[]) => {
    goToNextTrailStep({ kind: 'phrases', cats: currentCats });
  }, [goToNextTrailStep]);

  const checkReviewDue = useCallback(() => {
    setReviewDue(revisionProgress.wordsToReview.size > 0);
    // reviewDueCount est mis à jour précisément par onDueWordsCountChange (RevisionMode).
    // Ici on ne touche pas reviewDueCount pour ne pas écraser une valeur plus précise.
  }, [revisionProgress]);

  // Synchronise le badge dès que wordsToReview change (ex: après addWordsToReview suite
  // à une session d'apprentissage), sans attendre que l'utilisateur ouvre RevisionMode.
  // lastActiveTab est inclus pour restaurer le badge si RevisionMode l'avait mis à false
  // (via onDueWordsCountChange(0)) alors que wordsToReview n'est pas vide.
  useEffect(() => {
    const size = revisionProgress.wordsToReview.size;
    if (size > 0) {
      setReviewDue(true);
      // Si reviewDueCount vaut 0 (RevisionMode avait dit "rien à réviser") mais que de
      // nouveaux mots viennent d'être ajoutés, on invalide le cache pour que le badge
      // retombe sur wordsToReview.size jusqu'à la prochaine ouverture de RevisionMode.
      setReviewDueCount(prev => (prev === 0 ? null : prev));
    }
  }, [revisionProgress.wordsToReview.size, lastActiveTab]);

  // Nombre affiché dans les badges : compte exact remonté par RevisionMode, sinon
  // (onglet Réviser pas encore ouvert pour cette langue) la taille de wordsToReview.
  const displayedReviewCount = reviewDueCount ?? revisionProgress.wordsToReview.size;

  // Rafraîchit le badge quand l'utilisateur quitte l'onglet révision en vue mobile.
  // En vue mobile, mode reste 'menu' lors des changements d'onglet, donc selectMode('menu')
  // fait un early-return sans appeler refreshRevisionProgress. Ce useEffect comble ce manque.
  const prevLastActiveTabRef = useRef<MainTab>(lastActiveTab);
  useEffect(() => {
    const prev = prevLastActiveTabRef.current;
    prevLastActiveTabRef.current = lastActiveTab;
    if (prev === 'review' && lastActiveTab !== 'review' && languageData) {
      refreshRevisionProgress(new Set(Object.keys(languageData.categories)));
    }
  }, [lastActiveTab, languageData, refreshRevisionProgress]);

  // Comble l'écart entre wordsToReview.size (badge) et getDueWordsForCategory (RevisionMode).
  // Ajoute les mots appris absents de wordsToReview dès que userProgress et le hook sont prêts.
  useEffect(() => {
    if (!isRevisionProgressLoading && userProgress.recentlyLearnedWords.length > 0) {
      syncWithLearnedWords(userProgress.recentlyLearnedWords);
    }
  }, [userProgress.recentlyLearnedWords, isRevisionProgressLoading, syncWithLearnedWords]);

  const getTitleForMode = (mode: AppMode): string => {
    switch(mode) {
      case 'menu': return 'JogoLinga';
      case 'learn': return 'Vocabulaire';
      case 'review': return 'Revision';
      case 'quiz': return 'Quiz';
      case 'grammar': return 'Grammaire';
      case 'sentenceConstruction': return 'Construction de phrases';
      case 'sentenceGap': return 'Phrases e  trous';
      case 'exercise': return 'Exercices';
      case 'minigames': return 'Mini-jeux';
      case 'wordle': return 'Mot du jour';
      case 'memoryDuel': return 'Memory Duel';
      case 'forge': return 'La forge des mots';
      case 'weaver': return 'Le tisserand';
      default: return 'JogoLinga';
    }
  };


 
 //  FIX 3: Modifier selectMode pour eviter les verifications inutiles
const selectMode = useCallback((selectedMode: AppMode): void => {


  if (mode === selectedMode) {

    return;
  }

  // Ouvrir Mode Défi : fermer le toast uniquement (le badge reste jusqu'au retour au menu)
  if (selectedMode === 'challenge') {
    setChallengeToast(null);
  }
  // Retour au menu : rafraîchir le compteur défis + les révisions dues.
  // On exclut 'profile' : aucune révision ne peut y avoir lieu, le recalcul
  // viderait à tort wordsToReview (les mots encore dus disparaîtraient du badge).
  if (selectedMode === 'menu' && mode !== 'profile') {
    refreshRevisionProgress(new Set(Object.keys(languageData.categories)));
    if (user?.id) {
      getPendingChallenges(user.id)
        .then(list => setPendingChallengesCount(list.length))
        .catch(() => {});
    }
  }

  const currentTier = subscriptionService.getCurrentTier();
 
  
  if (currentTier === SubscriptionTier.PREMIUM) {
    setBlockedFeature(null);
    setPremiumFeatureLabel('');
    setShowSubscriptionModal(false);
    
  }

  if (selectedMode === 'menu') {
  setSelectedCategory(null); //  Seulement au retour au menu
}

  //  CORRECTION MAJEURE: Modes GRATUITS illimites
  const freeUnlimitedModes = ['menu', 'learn', 'review', 'quiz', 'progression', 'challenge', 'minigames', 'dialogue', 'wordle', 'memoryDuel'];
  const isPremiumOnlyMode = !freeUnlimitedModes.includes(selectedMode);

 if (isPremiumOnlyMode && currentTier !== SubscriptionTier.PREMIUM) {
  const premiumOnlyFeatures: Partial<Record<AppMode, string>> = {
    'grammar': 'grammar_full',
    'sentenceConstruction': 'sentence_construction',
    'sentenceGap': 'sentence_gap',
    'exercise': 'exercise_unlimited',
    'forge': 'forge_game',
    'weaver': 'weaver_game'
  };
  const requiredFeature = premiumOnlyFeatures[selectedMode];
  if (requiredFeature) {
    setBlockedFeature(requiredFeature);
    setPremiumFeatureLabel(getTitleForMode(selectedMode));
    setShowSubscriptionModal(true);
    return;
  }
}

  // Note : l'ancienne classe CSS 'mode-transition' (et le délai de 150ms qui
  // l'accompagnait) ne correspond plus à aucune règle de style — elle ne
  // produisait donc plus aucun effet visuel, seulement un retard inutile
  // avant l'affichage de la nouvelle vue (cause du flash au changement d'onglet).
  const root = document.getElementById('root');
  if (pendingTrailRestore.current !== null && (selectedMode === 'learn' || selectedMode === 'menu')) {
    const targetY = pendingTrailRestore.current;
    window.scrollTo({ top: targetY, behavior: 'instant' as ScrollBehavior });
    document.documentElement.scrollTop = targetY;
    document.body.scrollTop = targetY;
    if (root) root.scrollTop = targetY;
  } else if (skipMenuScrollReset.current) {
    // Le sentier (CategorySelection) repositionne lui-même le scroll sur le noeud actif
    // a son montage : ne pas le forcer a 0 ici pour eviter un flash au sommet.
    skipMenuScrollReset.current = false;
  } else {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (root) root.scrollTop = 0;
  }

  if (selectedMode !== 'menu') {
    setLastActiveTab(getModeTab(selectedMode));
  }
  setMode(selectedMode);
  setNavigationHistory(prev => [...prev, selectedMode]);

  if (selectedMode === 'learn') {
    setCurrentStreak(0);
    setLastAnswerCorrect(null);
  } else if (selectedMode === 'menu') {
    const isComingFromSummary = navigationHistory[navigationHistory.length - 1] === 'learn' && selectedCategory;
    if (!isComingFromSummary) {
      setSelectedCategory(null);
    }
  }
}, [
  mode,
  subscriptionService,
  subscriptionTier,
  setBlockedFeature,
  setPremiumFeatureLabel,
  setShowSubscriptionModal,
  getTitleForMode,
  navigationHistory,
  selectedCategory,
  isMobileView,
  refreshRevisionProgress,
  user?.id,
]);

  // Retour d'un mini-jeu : vers l'onglet Pratiquer s'il a été ouvert depuis là, sinon chemin habituel
  const backFromGame = useCallback((fallback: () => void) => {
    if (openedFromPractice.current) {
      openedFromPractice.current = false;
      setLastActiveTab('games');
      selectMode('menu');
    } else {
      fallback();
    }
  }, [selectMode]);

  useEffect(() => {
    if (mode === 'menu') { openedFromPractice.current = false; memoryFromChallenge.current = false; setCulturalQuizOnly(false); }
  }, [mode]);

selectModeRef.current = selectMode;

  useLayoutEffect(() => {
    if (pendingTrailRestore.current !== null && (mode === 'learn' || mode === 'menu')) {
      const targetY = pendingTrailRestore.current;
      if (mode === 'menu') pendingTrailRestore.current = null;
      window.scrollTo({ top: targetY, behavior: 'instant' as ScrollBehavior });
      document.body.scrollTop = targetY;
      document.documentElement.scrollTop = targetY;
      const rootEl = document.getElementById('root');
      if (rootEl) rootEl.scrollTop = targetY;
      return;
    }
    if (pendingTrailRestore.current !== null && mode !== 'grammar' && mode !== 'sentenceConstruction') {
      pendingTrailRestore.current = null;
    }
    // Le sentier (CategorySelection) repositionne lui-même le scroll sur le noeud actif
    // a son montage : ne pas le forcer a 0 ici pour eviter un flash au sommet (cf. effet ci-dessous).
    if (mode === 'menu' && lastActiveTab === 'learn') return;
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [mode, lastActiveTab]);

  // Reset scroll on tab switch. Skip 'learn' — its restoration is handled by useLayoutEffect([mode]).
  useLayoutEffect(() => {
    if (lastActiveTab === 'learn') return;
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [lastActiveTab]);

  const handleDesktopTabSelect = useCallback((tab: MainTab) => {
    if (tab === 'learn') setSelectedCategory(null);
    setLastActiveTab(tab);
    selectMode('menu');
  }, [selectMode]);




 useEffect(() => {


    const lastUsedLanguage = getLastUsedLanguage();
    const hasEverUsedApp = localStorage.getItem('hasEverUsedApp');
    
    if (!hasEverUsedApp && !lastUsedLanguage) {
  
      setIsFirstConnection(true);
      setShowLanguageSelection(true);
      setCurrentLanguage(null);
    } else if (lastUsedLanguage) {
      
      setCurrentLanguage(lastUsedLanguage);
      setIsFirstConnection(false);
      setShowLanguageSelection(false);
      
      // Charger les donnees de la langue (chunk Webpack séparé par langue)
      getLanguageDataAsync(lastUsedLanguage).then(setLanguageData);
    } else {

      setIsFirstConnection(true);
      setShowLanguageSelection(true);
      setCurrentLanguage(null);
    }
  }, []);

  // Déclenche le modal de partage après chaque gain XP (hors onboarding)
  useEffect(() => {
    const handleXP = (e: Event) => {
      const detail = (e as CustomEvent).detail as { newTotal: number; gained: number; source?: string } | undefined;
      if (!detail || detail.source === 'onboarding' || detail.source === 'load_progress') return;

      const langCode = currentLanguage ?? 'wf';
      const langNames: Record<string, string> = {
        wf: 'Wolof', ba: 'Bambara', la: 'Lingala', ff: 'Peul', co: 'Copte', sw: 'Swahili', yo: 'Yoruba',
      };
      const langFlags: Record<string, string> = {
        wf: '🇸🇳', ba: '🇲🇱', la: '🇨🇩', ff: '🇬🇳', co: '🇪🇬', sw: '🇹🇿', yo: '🇳🇬',
      };
      const rawStreak = localStorage.getItem('jogolinga-daily-streak');
      const streak = rawStreak ? (JSON.parse(rawStreak).streak ?? 0) : 0;

      setShareCardData({
        languageName: langNames[langCode] ?? 'Wolof',
        languageFlag: langFlags[langCode] ?? '🌍',
        wordsLearned: sessionLearnedWords.size || 1,
        streakDays: streak,
        totalXP: detail.newTotal,
        xpGained: detail.gained,
      });
    };
    window.addEventListener('xpUpdated', handleXP);
    return () => window.removeEventListener('xpUpdated', handleXP);
  }, [currentLanguage, sessionLearnedWords.size]);

  // Enregistre le service worker + planifie les notifs natives Capacitor
  useEffect(() => {
    registerServiceWorker();
    const hasEverUsedApp = localStorage.getItem('hasEverUsedApp');
    if (!hasEverUsedApp) return;
    try {
      const raw = localStorage.getItem('jogolinga-daily-streak');
      const streak = raw ? (JSON.parse(raw).streak ?? 0) : 0;
      initNotifications(streak);
    } catch {
      initNotifications(0);
    }
  }, []);




  useEffect(() => {
    if (selectedCategory === null && mode === 'learn') {
      setLastActiveTab('learn');
      setMode('menu');
    }
  }, [selectedCategory, mode]);

  // L'onglet « profile » du menu est un ancien écran (liste « Mon Profil ») :
  // l'onglet Profil doit toujours afficher la page Profil.
  useEffect(() => {
    if (mode === 'menu' && lastActiveTab === 'profile') setMode('profile');
  }, [mode, lastActiveTab]);

  // Navigation initiale vers Yoon (CategorySelection) dès que la langue est disponible
  const hasInitialNavigated = useRef(false);
  useEffect(() => {
    if (currentLanguage && languageData && !hasInitialNavigated.current) {
      hasInitialNavigated.current = true;
      if (isMobileView) {
        setLastActiveTab('learn');
        selectMode('menu');
      } else {
        selectMode('learn');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage, languageData]);


useEffect(() => {
  // Protection contre les appels multiples
  if (!currentLanguage) {
  
    return;
  }
  
  // Verifier si dejÃ  charge recemment (debounce)
  const lastLoad = parseInt(localStorage.getItem('last-progress-load') || '0');
  const now = Date.now();
  
  if (now - lastLoad < 2000) { // Pas plus d'une fois toutes les 2 secondes
   
    return;
  }
  
  
  localStorage.setItem('last-progress-load', now.toString());
  
  loadUserProgress();
  
}, [currentLanguage]); //  UNIQUEMENT currentLanguage dans les deps


  useEffect(() => {
    checkReviewDue();
  }, [checkReviewDue]);

useEffect(() => {
  if (!currentLanguage) {

    return;
  }
 
  getLanguageDataAsync(currentLanguage)
    .then(setLanguageData)
    .catch(() => {
    });
}, [currentLanguage]);

  // Masquer la landing page en mobile si un token existe (doit être dans un useEffect, pas dans le rendu)
  useEffect(() => {
    if (isMobileView && showLandingPage) {
      const hasToken = localStorage.getItem('googleToken');
      if (hasToken) setShowLandingPage(false);
    }
  }, [isMobileView, showLandingPage]);

  // Masquer la page de connexion dès qu'une session Supabase valide est détectée au chargement,
  // sans passer par le cooldown de handleLogin.
  useEffect(() => {
    if (!showLandingPage) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setShowLandingPage(false);
    });
  }, []);

  const [isLayoutTransitioning, setIsLayoutTransitioning] = useState(false);
  const currentIsMobileRef = React.useRef(window.innerWidth <= 768);

  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout>;
    let transitionTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      clearTimeout(transitionTimer);
      const nextMobile = window.innerWidth <= 768;
      // Déclencher la transition seulement si le breakpoint change (desktop↔mobile)
      if (nextMobile !== currentIsMobileRef.current) {
        setIsLayoutTransitioning(true);
      }
      resizeTimer = setTimeout(() => {
        const mobile = window.innerWidth <= 768;
        currentIsMobileRef.current = mobile;
        setIsMobileView(mobile);
        if (mobile) {
          document.body.classList.add('mobile-view');
        } else {
          document.body.classList.remove('mobile-view');
        }
        transitionTimer = setTimeout(() => setIsLayoutTransitioning(false), 50);
      }, 150);
    };

    // Initialiser immédiatement
    const mobile = window.innerWidth <= 768;
    currentIsMobileRef.current = mobile;
    setIsMobileView(mobile);
    if (mobile) document.body.classList.add('mobile-view');
    else document.body.classList.remove('mobile-view');

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  useEffect(() => {
    // Charger l'etat d'abonnement au demarrage
    const tier = subscriptionService.getCurrentTier();
  
    setSubscriptionTier(tier);
  }, []);

  useEffect(() => {
    // Initialiser revisionProgressState avec les donnees de revisionProgress
    setRevisionProgress(revisionProgress);
  }, [revisionProgress]);

  useEffect(() => {
    // Appliquer immediatement
    removeWhiteHeader();
    
    // eâ€°couter les evenements de scroll qui pourraient faire reapparaitre l'element blanc
    const handleScroll = () => {
      if (window.scrollY <= 10) {
        removeWhiteHeader();
      }
    };
    
    // ecouter les evenements de redimensionnement (isMobileView géré dans un useEffect dédié)
    const handleResize = () => {
      removeWhiteHeader();
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Executer egalement apres un court delai pour s'assurer que tous les elements sont charges
    const timeoutId = setTimeout(removeWhiteHeader, 500);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Filet de sécurité : sur certains WebViews embarqués (ex. navigateur
  // in-app Gmail), le header et la nav du bas (position: fixed) peuvent se
  // peindre à une position/zone de clic périmée après le premier chargement
  // (chrome du WebView qui se stabilise, retour au premier plan, rotation),
  // glitch qui restait bloqué jusqu'au rechargement manuel de la page. On
  // force un reflow de ces éléments aux moments où ce désync est probable.
  useEffect(() => {
    const nudgeFixedElements = () => {
      ['.mobile-header', '.egyptian-bottom-nav'].forEach(selector => {
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) return;
        const prevPosition = el.style.position;
        el.style.position = 'static';
        void el.offsetHeight;
        el.style.position = prevPosition;
      });
    };

    const timeoutId = setTimeout(nudgeFixedElements, 300);
    window.addEventListener('pageshow', nudgeFixedElements);
    window.addEventListener('orientationchange', nudgeFixedElements);
    document.addEventListener('visibilitychange', nudgeFixedElements);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('pageshow', nudgeFixedElements);
      window.removeEventListener('orientationchange', nudgeFixedElements);
      document.removeEventListener('visibilitychange', nudgeFixedElements);
    };
  }, []);

  // Filet de sécurité : ConfirmationModal verrouille le scroll du body
  // (overflow:hidden + position:fixed) tant qu'il est ouvert, et le
  // déverrouille via le cleanup de son useEffect. Si l'onglet est mis en
  // arrière-plan (changement d'onglet, veille) pendant que ce cleanup est
  // censé s'exécuter, le navigateur peut suspendre l'exécution JS avant
  // qu'il n'ait eu lieu : au retour sur l'onglet, le verrou reste
  // appliqué alors qu'aucune modale n'est plus affichée, bloquant tout
  // scroll jusqu'à un rechargement manuel. On revérifie donc à chaque
  // retour au premier plan qu'un verrou actif correspond bien à une
  // modale de confirmation réellement présente dans le DOM.
  useEffect(() => {
    const releaseStaleScrollLock = () => {
      const modalPresent = document.querySelector('[data-modal-type="confirmation"]');
      if (!modalPresent && document.body.style.position === 'fixed') {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.top = '';
      }
    };

    window.addEventListener('pageshow', releaseStaleScrollLock);
    document.addEventListener('visibilitychange', releaseStaleScrollLock);

    return () => {
      window.removeEventListener('pageshow', releaseStaleScrollLock);
      document.removeEventListener('visibilitychange', releaseStaleScrollLock);
    };
  }, []);

  // SESSION_DATE est figée au chargement du module. Sur Android/PWA, la WebView
  // survit en arrière-plan pendant des jours : l'activité et l'XP du lendemain
  // étaient attribués à la veille (objectif du jour « déjà atteint », jour absent
  // de l'historique du streak). Au retour au premier plan un autre jour, après
  // une vraie absence, on recharge pour repartir sur la bonne date. Une session
  // active qui passe minuit n'est pas concernée (seuil d'absence).
  useEffect(() => {
    const MIN_AWAY_MS = 10 * 60 * 1000;
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let hiddenAt: number | null = document.hidden ? Date.now() : null;
    const onVisibility = () => {
      if (document.hidden) { hiddenAt = Date.now(); return; }
      const awayMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (awayMs >= MIN_AWAY_MS && toLocal(new Date()) !== SESSION_DATE) {
        window.location.reload();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    // Initialiser le service de paiement
    paymentService.initialize().catch(() => {});
  }, []);

 
  useEffect(() => {
    const tier = subscriptionService.getCurrentTier();
    setSubscriptionTier(tier);
    
    // Verifier si on est sur la page de paiement reussi
    if (location.pathname === '/payment-success') {
   
      
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');
      
      if (sessionId) {
    
        
        paymentService.verifyPayment(sessionId).then(success => {
          if (success) {
          
            
            //  AJOUT: Reset du modal apres verification reussie
            setSubscriptionTier(SubscriptionTier.PREMIUM);
            setShowSubscriptionModal(false);
            setBlockedFeature(null);
            setPremiumFeatureLabel('');
          }
        }).catch(() => {
        });
      }
    }
  }, [location]);



  // FIX 5: Ajouter un useEffect pour surveiller les evenements subscription
useEffect(() => {
  const handleSubscriptionUpdate = (event: CustomEvent) => {
   
    
    const newTier = event.detail.tier;
    

    setSubscriptionTier(newTier);
    
    if (newTier === SubscriptionTier.PREMIUM) {
   
      setShowSubscriptionModal(false);
      setBlockedFeature(null);
      setPremiumFeatureLabel('');
    }
  };

  window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate as EventListener);
  
  return () => {
    window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate as EventListener);
  };
}, [setSubscriptionTier]); 
  
 

  

useEffect(() => {
  const appElement = document.querySelector('.App');
  if (appElement) {
    if (mode === 'exercise') {
      appElement.classList.add('has-exercise-mode');
    } else {
      appElement.classList.remove('has-exercise-mode');
    }
  }
  
  return () => {
    const appElement = document.querySelector('.App');
    if (appElement) {
      appElement.classList.remove('has-exercise-mode');
    }
  };
}, [mode]);

useEffect(() => {
  const handleAuthStatusChange = (event: CustomEvent) => {
  
    
    const { isAuthenticated, user: eventUser } = event.detail;
    
    if (isAuthenticated && eventUser) {
     
      
      // Attendre un peu que tout se stabilise
      setTimeout(() => {
        
        
        // Forcer le rechargement du cache
        subscriptionService.reloadSubscription();
        
        // Mettre Ã  jour le tier dans le state
        const newTier = subscriptionService.getCurrentTier();
     
        
        setSubscriptionTier(newTier);
        
        // Fermer le modal si c'etait ouvert
        if (newTier === SubscriptionTier.PREMIUM) {
         
          setShowSubscriptionModal(false);
          setBlockedFeature(null);
          setPremiumFeatureLabel('');
        }
      }, 500); // Delai pour laisser le cache se synchroniser
      
    } else {
    
      

      subscriptionService.clearCache();
      setSubscriptionTier(SubscriptionTier.FREE);
      setShowSubscriptionModal(false);
      setBlockedFeature(null);
      setPremiumFeatureLabel('');
    }
  };


  window.addEventListener('authStatusChanged', handleAuthStatusChange as EventListener);
  
  // Cleanup
  return () => {
    window.removeEventListener('authStatusChanged', handleAuthStatusChange as EventListener);
  };
}, [setSubscriptionTier, setShowSubscriptionModal, setBlockedFeature, setPremiumFeatureLabel]);

// Fix LandingPage mobile : déplacé dans un useEffect pour éviter les setState en render
useEffect(() => {
  if (!isMobileView || !showLandingPage) return;
  const hasToken = localStorage.getItem('googleToken');
  if (!hasToken) return;
  setShowLandingPage(false);
  if (!currentLanguage) {
    setShowLanguageSelection(true);
    setIsFirstConnection(true);
  }
}, [isMobileView, showLandingPage, currentLanguage]);

// Pages publiques (contenu éditorial, SEO/AdSense) : toujours rendues via
// <Routes> ci-dessous, quel que soit l'état de sélection de langue — sinon
// un visiteur/crawler arrivant directement sur /guide/wolof, par exemple,
// ne voit jamais que l'écran "choisissez une langue" ou "Chargement...".
// Seules les URLs de l'application elle-même passent par l'écran de langue ;
// toute autre URL (pages éditoriales, /apprendre/..., URL inconnue → 404)
// est rendue directement par <Routes>.
// /subscription : URL d'annulation du paiement Stripe (secureAuthService).
const APP_ROUTES = ['/', '/index.html', '/subscription'];
const isAppRoute = APP_ROUTES.includes(location.pathname);
const isPublicRoute = !isAppRoute;

// La sélection de langue s'affiche UNIQUEMENT lors de la première connexion
if (showLanguageSelection && isFirstConnection && !showLandingPage && !isPublicRoute) {

  return (
    <ThemeProvider>
      <LanguageFirstSelection
        onLanguageSelect={handleLanguageFirstSelection}
        isVisible={true}
      />
    </ThemeProvider>
  );
}

  //  CONDITION 2 : Protection contre rendu sans langue (DEUXIEME PRIORITEE)
  if ((!currentLanguage || !languageData) && !showLandingPage && !isPublicRoute) {
    return (
      <ThemeProvider>
        <div className="app-loading-screen">
          <div>
            <div className="app-loading-screen__emoji"><Picto name="mondeLangues" /></div>
            <div>Chargement de l'application...</div>
          </div>
        </div>
      </ThemeProvider>
    );
  }




  //  RENDU PRINCIPAL (TROISIEME PRIORITEE - seulement si langue selectionnee)
  return (
  <ThemeProvider>
    <>
    <AndroidAppBanner />
    <CookieConsentBanner />
    <Routes>
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/about" element={<About />} />
      <Route path="/guide/wolof" element={<GuideWolof />} />
      <Route path="/guide/bambara" element={<GuideBambara />} />
      <Route path="/guide/swahili" element={<GuideSwahili />} />
      <Route
        path="/apprendre/*"
        element={
          <React.Suspense fallback={<div className="app-loading-screen" />}>
            <LearnRoutes />
          </React.Suspense>
        }
      />
      <Route
        path="/articles/*"
        element={
          <React.Suspense fallback={<div className="app-loading-screen" />}>
            <ArticleRoutes />
          </React.Suspense>
        }
      />

      {/*  Routes pour la page de succès de paiement */}
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/successpage" element={<PaymentSuccessPage />} />

      {/*  Route par defaut qui reprend tout le contenu (URL inconnue → 404) */}
      <Route
        path="/*"
        element={
          !isAppRoute ? <NotFound /> : (
          <>
            {/* Écran de transition pendant le switch desktop↔mobile */}
            {isLayoutTransitioning ? <div className="layout-transition-overlay" /> : null}

            {/* Page de connexion mobile */}
            {showLandingPage && isMobileView && !isPaymentSuccessPage && !guestMode && (
              <MobileLoginPage
                onLogin={handleLogin}
                onContinueAsGuest={() => {
                  localStorage.setItem('guestMode', 'true');
                  setGuestMode(true);
                  setShowLandingPage(false);
                  if (!currentLanguage) {
                    setShowLanguageSelection(true);
                    setIsFirstConnection(true);
                  }
                }}
              />
            )}

            {/* LandingPage desktop */}
            {showLandingPage && !isMobileView && !isPaymentSuccessPage ? (
              <LandingPage
                onLogin={handleLogin}
                onContinueAsGuest={() => {
                  setShowLandingPage(false);
                  if (!currentLanguage) {
                    setShowLanguageSelection(true);
                    setIsFirstConnection(true);
                  }
                }}
              />
            ) : (
              <motion.div 
                className="App"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}

              >
                {/* Contenu principal */}
                {isMobileView ? (
                  <div className="mobile-app-container">
                    {/* Header mobile - TOUJOURS visible */}
                    <MobileHeader
                      title=""
                      languageCode={currentLanguage}
                      onLanguageChange={handleLanguageChange}
                      showLanguageSelector={true}
                      currentMode={mode}
                      onLogin={handleLogin}
                      onLogout={handleLogout}
                      onOpenSubscription={handleOpenSubscription}
                      subscriptionTier={subscriptionTier}
                      user={user}
                      onStreakClick={() => {
                        const raw = localStorage.getItem('jogolinga-daily-streak');
                        const s = raw ? (JSON.parse(raw).streak ?? 0) : 0;
                        setStreakModalCount(s);
                        setShowStreakModal(true);
                      }}
                    />
                    
                    {/* CONTENU PRINCIPAL avec verifications */}
                    <div className="mobile-main-content">
                      
                    
                      
                      {/*  MENU - Router principal par onglet  */}
                      {(!mode || mode === 'menu') && (
                        <>
                          {/* Onglet Apprendre */}
                          {lastActiveTab === 'learn' && !selectedCategory && (
                            <div className="mode-wrapper">
                              <CategorySelection
                                languageCode={currentLanguage ?? languageData.code}
                                categories={languageData.categories}
                                categoryIllustrations={languageData.categoryIllustrations}
                                onSelectCategory={handleLearnCategorySelect}
                                onStartRevision={handleStartRevision}
                                completedCategories={completedCategories}
                                sessionLearnedWords={sessionLearnedWords}
                                userProgress={userProgress}
                                isMobileView={true}
                                onSelectGrammar={(unit, subcatFilter) => {
                                  const scrollY = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                                  trailScrollY.current = scrollY;
                                  setGrammarUnitFilter(unit);
                                  setGrammarSubcategoryFilter(subcatFilter ?? null);
                                  selectMode('grammar');
                                }}
                                onSelectConstruction={(cats) => {
                                  const scrollY = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                                  trailScrollY.current = scrollY;
                                  setConstructionFilterCategories(cats);
                                  selectMode('sentenceConstruction');
                                }}
                                subscriptionTier={subscriptionTier}
                                onOpenSubscription={handleOpenSubscription}
                                onOpenMiniGame={(game) => {
                                  trailScrollY.current = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                                  if (game === 'challenge') { selectMode('challenge'); }
                                  else if (game === 'music') { selectMode('songAnalyzer'); }
                                  else { selectMode('minigames'); }
                                }}
                                onSelectQuizTheme={(t) => {
                                  trailScrollY.current = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                                  setInitialQuizTheme(t);
                                  selectMode('minigames');
                                }}
                                pendingChallengesCount={pendingChallengesCount}
                                activationKey={trailActivationKey}
                                onSelectConversation={(convId) => {
                                  trailScrollY.current = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                                  setConversationId(convId);
                                  selectMode('dialogue');
                                }}
                                onKamaLessonComplete={handleKamaLessonComplete}
                                onKamaAnswer={handleQuizAnswer}
                                isKamaWordTestable={isKamaWordTestable}
                              />
                            </div>
                          )}

                          {/* Onglet Réviser */}
                          {lastActiveTab === 'review' && (
                            <div className="mode-wrapper">
                              <RevisionMode
                                languageCode={currentLanguage}
                                onBackToMenu={() => setLastActiveTab('review')}
                                onStartLearning={() => setLastActiveTab('learn')}
                                onWordRevised={(_word: string, isCorrect: boolean) => {
                                  setCurrentStreak(isCorrect ? prev => prev + 1 : 0);
                                  setLastAnswerCorrect(isCorrect);
                                }}
                                onSessionComplete={() => {
                                  setSessionLearnedWords(prev => new Set([...prev, `revision-${Date.now()}`]));
                                }}
                                onUpdateRevisionState={handleUpdateRevisionState}
                                onDueWordsCountChange={(count) => {
                                  setReviewDueCount(count);
                                  setReviewDue(count > 0);
                                  try { localStorage.setItem(`${currentLanguage}-reviewDueCount`, String(count)); } catch {}
                                }}
                                onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                              />
                            </div>
                          )}

                          {/* Onglet Pratiquer + fallback */}
                          {(lastActiveTab === 'games' || lastActiveTab === 'profile' || !['learn','review'].includes(lastActiveTab)) && (
                            <div className="mode-wrapper mode-wrapper--full-height">
                              <MainMenu
                                languageCode={currentLanguage}
                                onSelectMode={(selectedMode) => { openedFromPractice.current = true; setCulturalQuizOnly(selectedMode === 'minigames'); selectMode(selectedMode); }}
                                reviewDue={reviewDue}
                                reviewDueCount={displayedReviewCount}
                                onResetProgress={handleResetProgress}
                                learnedWordsCount={getUniqueLearnedWordsCount()}
                                onLanguageChange={handleLanguageChange}
                                isMobileView={true}
                                revisionProgress={revisionProgressState}
                                onOpenSubscription={handleOpenSubscription}
                                onLogin={handleLogin}
                                onLogout={handleLogout}
                                subscriptionTier={subscriptionTier}
                                initialTab={lastActiveTab}
                                pendingChallengesCount={pendingChallengesCount}
                              />
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* MINI-JEUX - accès depuis le sentier */}
                      {mode === 'minigames' && (
                        <div className="mode-wrapper">
                          <MiniGames
                            languageCode={currentLanguage}
                            onChallengeFriend={culturalQuizOnly ? undefined : () => selectMode('challenge')}
                            pendingChallengesCount={pendingChallengesCount}
                            onLaunchSongAnalyzer={culturalQuizOnly ? undefined : () => selectMode('songAnalyzer')}
                            onLaunchMemoryDuel={culturalQuizOnly ? undefined : () => selectMode('memoryDuel')}
                            onLaunchForge={culturalQuizOnly ? undefined : () => selectMode('forge')}
                            initialTheme={initialQuizTheme ?? undefined}
                            onBack={() => { setInitialQuizTheme(null); backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('menu'); }); }}
                          />
                        </div>
                      )}

                      {/*  APPRENTISSAGE - Avec double protection */}
                      {mode === 'learn' && selectedCategory && (
                        <div className="mode-wrapper">
                       
                          <LearnMode
                            languageCode={currentLanguage}
                            category={selectedCategory}
                            onBackToCategories={handleBackFromLearn}
                            onGameComplete={() => handleCategoryComplete(selectedCategory)}
                            onLessonSummary={maybeShowSessionAd}
                            onWordLearned={(word: string, correct: boolean) =>
                              updateWordProgress(selectedCategory, word, correct)}
                            onWordsLearned={(words) => {
                              handleWordsLearned(words);
                              setSessionLearnedWords(prev =>
                                new Set([...prev, ...words.map(w => w.word)])
                              );

                            }}
                            resetToken={resetToken}
                            onAnswer={handleQuizAnswer}
                            isLoading={isLoading}
                            sessionLearnedWords={sessionLearnedWords}
                            setMode={setMode}
                            userProgress={userProgress}
                            saveProgressOnSummary={saveProgressOnSummary}
                            wordRange={selectedWordRange ?? undefined}
                            onNextCategory={handleNextCategory}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        </div>
                      )}


                      {/*  QUIZ */}
                      {mode === 'quiz' && (
                        <div className="mode-wrapper">
                          {currentLanguage === 'ka' ? (
                            <KamaSigneDirecteurExercise
                              title={t('app.kamaGraphieQuizTitle')}
                              items={graphieReconstructionPractice}
                              onBackToCategories={() => selectMode("menu")}
                              onExerciseComplete={handleSuccess}
                              onAnswer={handleQuizAnswer}
                              timed
                            />
                          ) : (
                            <QuizComponent
                              languageCode={currentLanguage}
                              words={transformedWords}
                              onComplete={handleSuccess}
                              onAnswerSubmit={handleQuizAnswer}
                              backgroundAudioUrl="/ambiance/Chronometre.wav"
                              correctAnswerSoundUrl="/ambiance/Correct.mp3"
                              wrongAnswerSoundUrl="/ambiance/Erreur.mp3"
                              onGameFinished={() => selectMode('menu')}
                            />
                          )}
                        </div>
                      )}

                      {/* MODE DÉFI */}
                      {mode === 'challenge' && (
                        <div className="mode-wrapper">
                          <ChallengeMode
                            onBackToMenu={() => { setChallengeTargetFriendId(null); backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('menu'); }); }}
                            user={user}
                            initialFriendId={challengeTargetFriendId}
                            languageCode={currentLanguage ?? languageData.code}
                            onOpenMemoryDuel={() => { memoryFromChallenge.current = true; selectMode('memoryDuel'); }}
                          />
                        </div>
                      )}

                      {/* CONVERSATIONS */}
                      {mode === 'dialogue' && (
                        <div className="mode-wrapper">
                          <ConversationGame
                            languageCode={currentLanguage}
                            onBack={() => { pendingTrailRestore.current = trailScrollY.current; setConversationId(null); setLastActiveTab('learn'); selectMode('menu'); }}
                            initialConversationId={conversationId ?? undefined}
                            onComplete={handleConversationComplete}
                          />
                        </div>
                      )}

                      {/* ANALYSE DE CHANSONS */}
                      {mode === 'songAnalyzer' && (
                        <div className="mode-wrapper">
                          <SongAnalyzer
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('menu'); })}
                          />
                        </div>
                      )}

                      {/* MOT DU JOUR (Wordle africain) */}
                      {mode === 'wordle' && (
                        <div className="mode-wrapper">
                          <WordleGame
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('minigames'); })}
                          />
                        </div>
                      )}

                      {/* LE TISSERAND */}
                      {mode === 'weaver' && (
                        <div className="mode-wrapper">
                          <TisserandGame
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { setLastActiveTab('games'); selectMode('menu'); })}
                          />
                        </div>
                      )}

                      {/* LA FORGE DES MOTS */}
                      {mode === 'forge' && (
                        <div className="mode-wrapper">
                          <ForgeGame
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('minigames'); })}
                          />
                        </div>
                      )}

                      {/* MEMORY DUEL */}
                      {mode === 'memoryDuel' && (
                        <div className="mode-wrapper">
                          <MemoryDuelGame
                            languageCode={currentLanguage}
                            user={user}
                            onBackToMenu={() => { if (memoryFromChallenge.current) { memoryFromChallenge.current = false; selectMode('challenge'); return; } backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('minigames'); }); }}
                          />
                        </div>
                      )}

                      {/* GRAMMAIRE */}
                      {mode === 'grammar' && (
                        <div className="mode-wrapper">

                          <GrammarMode
                            languageCode={currentLanguage}
                            onBackToCategories={() => { pendingTrailRestore.current = trailScrollY.current; setSelectedCategory(null); setLastActiveTab('learn'); selectMode('menu'); }}
                            onCategoryComplete={(category: string) => { handleCategoryComplete(category); }}
                            resetToken={resetToken}
                            onWordsLearned={(words) => {
                              handleWordsLearned(words);
                              setSessionLearnedWords(prev => new Set([...prev, ...words.map(w => w.word)]));
                            }}
                            saveProgressOnSummary={saveProgressOnSummary}
                            userProgress={userProgress}
                            isMobileView={true}
                            grammarUnit={grammarUnitFilter}
                            grammarSubcategoryFilter={grammarSubcategoryFilter ?? undefined}
                            onAnswerSubmit={handleQuizAnswer}
                            onStartRevision={() => selectMode('review')}
                            onFilterComplete={() => navigateAfterGrammar(grammarUnitFilter ?? 1, grammarSubcategoryFilter ?? null)}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        </div>
                      )}

                      {/* CONSTRUCTION DE PHRASES */}
                      {mode === 'sentenceConstruction' && (
                        <div className="mode-wrapper">

                          <SentenceConstructionGame
                            languageCode={currentLanguage}
                            onBackToCategories={() => {
                              pendingTrailRestore.current = trailScrollY.current;
                              selectMode('menu');
                            }}
                            onAnswer={handleQuizAnswer}
                            onGameComplete={() => navigateAfterPhrases(constructionFilterCategories)}
                            onSentencesLearned={() => { recordActivity(); }}
                            saveProgressOnSummary={saveProgressOnSummary}
                            userProgress={userProgress}
                            isMobileView={true}
                            filterCategories={constructionFilterCategories}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        </div>
                      )}

                      {/*  PHRASES A TROUS */}
                      {mode === 'sentenceGap' && (
                        <div className="mode-wrapper">
                        
                          <SentenceGapGame
                            languageCode={currentLanguage}
                            onGameComplete={handleSuccess}
                            onBackToCategories={() => selectMode("menu")}
                            onAnswer={handleQuizAnswer}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        </div>
                      )}

                      {/* EXERCICES */}
                      {mode === 'exercise' && (
                        <div className="mode-wrapper">
                          {currentLanguage === 'ka' ? (
                            <KamaSigneDirecteurExercise
                              title={t('app.kamaSigneDirecteurTitle')}
                              items={signeDirecteurPractice}
                              onBackToCategories={() => selectMode("menu")}
                              onExerciseComplete={handleSuccess}
                              onAnswer={handleQuizAnswer}
                            />
                          ) : (
                            <ExerciseMode
                              languageCode={currentLanguage}
                              onBackToCategories={() => selectMode("menu")}
                              onExerciseComplete={handleSuccess}
                              onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                            />
                          )}
                        </div>
                      )}

                      
                      {/* PROFIL */}
                      {mode === 'profile' && (
                        <div className="mode-wrapper mode-wrapper--full-height">
                          <ProfilePage
                            user={user}
                            userProgress={userProgress}
                            completedCategories={completedCategories}
                            availableCategories={Object.keys(languageData?.categories ?? {}).filter(c => !c.includes('_'))}
                            currentStreak={currentStreak}
                            currentLanguage={currentLanguage}
                            subscriptionTier={subscriptionTier}
                            onLogout={handleLogout}
                            onOpenSubscription={handleOpenSubscription}
                            onResetProgress={handleResetProgress}
                            onChallengeFriend={(friendId) => {
                              setChallengeTargetFriendId(friendId);
                              selectMode('challenge');
                            }}
                          />
                        </div>
                      )}

                      {/*  FALLBACK AMELIOREE */}
                      {mode && !['menu', 'learn', 'review', 'quiz', 'grammar', 'sentenceConstruction', 'sentenceGap', 'exercise', 'progression', 'profile', 'challenge', 'minigames', 'dialogue', 'songAnalyzer', 'wordle', 'memoryDuel', 'forge', 'weaver'].includes(mode) && (
                        <div className="mode-wrapper mode-wrapper--fallback">
                          <h2>Mode non reconnu: {mode}</h2>
                          <p>Mode supportes: menu, learn, review, quiz, grammar, sentenceConstruction, sentenceGap, exercise, progression</p>
                          <button 
                            onClick={() => {
                             
                              setMode('menu');
                              setSelectedCategory(null);
                            }}
                            className="quiz-button"
                          >
                            Retour au menu
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/*  NOUVEAU : Footer Mobile */}
                   {/*  Footer conditionnel */}
{mode === 'menu' && (
  <MobileFooter
    onNavigateToPrivacy={handleNavigateToPrivacy}
    onNavigateToTerms={handleNavigateToTerms}
  />
)}

{/* Barre de navigation persistante */}
{(user || hasLocalCredentials || guestMode) && !isPaymentSuccessPage && !(['learn', 'quiz', 'exercise', 'sentenceConstruction', 'sentenceGap', 'grammar', 'challenge', 'dialogue', 'forge', 'weaver'] as AppMode[]).includes(mode) && (() => {
  // Copte et Kama restent en hiéroglyphes (cohérent avec leur thème égyptien) ;
  // les autres langues utilisent les icônes au trait du nouveau design.
  const isEgyptianTheme = currentLanguage === 'co' || currentLanguage === 'ka';
  return (
  <div
    className="egyptian-bottom-nav"
  >
    <button
      className={`nav-item ${(mode === 'learn' || (mode === 'menu' && lastActiveTab === 'learn')) ? 'active' : ''}`}
      onClick={() => { skipMenuScrollReset.current = true; setLastActiveTab('learn'); setSelectedCategory(null); selectMode('menu'); }}
      style={{ position: 'relative' }}
    >
      <div className="nav-icon">{isEgyptianTheme ? '𓀁' : <BookOpen size={22} strokeWidth={1.75} />}</div><div className="nav-label">{t('mainMenu.sidebar.learn')}</div>
    </button>
    <button
      className={`nav-item ${(mode === 'menu' && lastActiveTab === 'review') ? 'active' : ''}`}
      onClick={() => { setLastActiveTab('review'); selectMode('menu'); }}
      style={{ position: 'relative' }}
    >
      <div className="nav-icon">{isEgyptianTheme ? '𓇳' : <RotateCcw size={22} strokeWidth={1.75} />}</div><div className="nav-label">{t('mainMenu.sidebar.review')}</div>
      {reviewDue && (
        <div style={{ position: 'absolute', top: '4px', right: '12px', background: 'linear-gradient(135deg, var(--color-error), var(--color-error-dark))', minWidth: '18px', height: '18px', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '0 4px', boxShadow: '0 0 6px rgba(239,68,68,0.5)' }}>
          {displayedReviewCount > 9 ? '9+' : displayedReviewCount}
        </div>
      )}
    </button>
    <button
      className={`nav-item ${(['quiz','exercise','challenge','dialogue','songAnalyzer','minigames','wordle','memoryDuel','forge','weaver'].includes(mode) || (mode === 'menu' && lastActiveTab === 'games')) ? 'active' : ''}`}
      onClick={() => { setLastActiveTab('games'); selectMode('menu'); }}
      style={{ position: 'relative' }}
    >
      <div className="nav-icon">{isEgyptianTheme ? '𓊃' : <Dumbbell size={22} strokeWidth={1.75} />}</div><div className="nav-label">{t('mainMenu.sidebar.games')}</div>
      {pendingChallengesCount > 0 && (
        <div style={{ position: 'absolute', top: '4px', right: '12px', background: 'linear-gradient(135deg, var(--color-error), var(--color-error-dark))', minWidth: '18px', height: '18px', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '0 4px', boxShadow: '0 0 6px rgba(239,68,68,0.5)' }}>
          {pendingChallengesCount > 9 ? '9+' : pendingChallengesCount}
        </div>
      )}
    </button>
    <button
      className={`nav-item ${(mode === 'profile' || (mode === 'menu' && lastActiveTab === 'profile')) ? 'active' : ''}`}
      onClick={() => selectMode('profile')}
    >
      <div className="nav-icon">{isEgyptianTheme ? '𓀀' : <UserIcon size={22} strokeWidth={1.75} />}</div><div className="nav-label">{t('mainMenu.sidebar.profile')}</div>
    </button>
  </div>
  );
})()}
                    
                  </div>
                ) : (
                  <div className="desktop-navigation-container">
                    <MainMenu
                      languageCode={currentLanguage}
                      onSelectMode={(selectedMode) => { openedFromPractice.current = true; setCulturalQuizOnly(selectedMode === 'minigames'); selectMode(selectedMode); }}
                      reviewDue={reviewDue}
                      reviewDueCount={displayedReviewCount}
                      onResetProgress={handleResetProgress}
                      learnedWordsCount={getUniqueLearnedWordsCount()}
                      onLanguageChange={handleLanguageChange}
                      isMobileView={false}
                      revisionProgress={revisionProgressState}
                      onOpenSubscription={handleOpenSubscription}
                      onLogin={handleLogin}
                      onLogout={handleLogout}
                      subscriptionTier={subscriptionTier}
                      initialTab={lastActiveTab}
                      onNavigateToPrivacy={handleNavigateToPrivacy}
                      onNavigateToTerms={handleNavigateToTerms}
                      currentAppMode={mode}
                      pendingChallengesCount={pendingChallengesCount}
                      onSelectTab={handleDesktopTabSelect}
                      desktopContent={(() => {
                        if ((mode === 'learn' || (mode === 'menu' && lastActiveTab === 'learn')) && !selectedCategory) return (
                          <CategorySelection
                            languageCode={currentLanguage ?? languageData.code}
                            categories={languageData.categories}
                            categoryIllustrations={languageData.categoryIllustrations}
                            onSelectCategory={handleLearnCategorySelect}
                            onStartRevision={handleStartRevision}
                            completedCategories={completedCategories}
                            sessionLearnedWords={sessionLearnedWords}
                            userProgress={userProgress}
                            isMobileView={false}
                            onSelectGrammar={(unit, subcatFilter) => {
                              const scrollY = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                              trailScrollY.current = scrollY;
                              setGrammarUnitFilter(unit);
                              setGrammarSubcategoryFilter(subcatFilter ?? null);
                              selectMode('grammar');
                            }}
                            onSelectConstruction={(cats) => {
                              const scrollY = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                              trailScrollY.current = scrollY;
                              setConstructionFilterCategories(cats);
                              selectMode('sentenceConstruction');
                            }}
                            subscriptionTier={subscriptionTier}
                            onOpenSubscription={handleOpenSubscription}
                            onOpenMiniGame={(game) => {
                              trailScrollY.current = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                              if (game === 'challenge') { selectMode('challenge'); }
                              else if (game === 'music') { selectMode('songAnalyzer'); }
                              else { selectMode('minigames'); }
                            }}
                            onSelectQuizTheme={(t) => {
                              trailScrollY.current = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                              setInitialQuizTheme(t);
                              selectMode('minigames');
                            }}
                            pendingChallengesCount={pendingChallengesCount}
                            activationKey={trailActivationKey}
                            onSelectConversation={(convId) => {
                              trailScrollY.current = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);
                              setConversationId(convId);
                              selectMode('dialogue');
                            }}
                            onKamaLessonComplete={handleKamaLessonComplete}
                            onKamaAnswer={handleQuizAnswer}
                            isKamaWordTestable={isKamaWordTestable}
                          />
                        );
                        if (mode === 'learn' && selectedCategory) return (
                          <LearnMode
                            languageCode={currentLanguage}
                            category={selectedCategory}
                            onBackToCategories={() => {
                              localStorage.removeItem(`${currentLanguage}-${selectedCategory}-summary-in-progress`);
                              handleBackFromLearn();
                            }}
                            onGameComplete={() => handleCategoryComplete(selectedCategory)}
                            onLessonSummary={maybeShowSessionAd}
                            onWordLearned={(word, correct) => updateWordProgress(selectedCategory, word, correct)}
                            onWordsLearned={(words) => {
                              handleWordsLearned(words);
                              setSessionLearnedWords(prev => new Set([...prev, ...words.map(w => w.word)]));
                            }}
                            resetToken={resetToken}
                            onAnswer={handleQuizAnswer}
                            isLoading={isLoading}
                            sessionLearnedWords={sessionLearnedWords}
                            setMode={setMode}
                            userProgress={userProgress}
                            saveProgressOnSummary={saveProgressOnSummary}
                            wordRange={selectedWordRange ?? undefined}
                            onNextCategory={handleNextCategory}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        );
                        if (mode === 'review' || (mode === 'menu' && lastActiveTab === 'review')) return (
                          <RevisionMode
                            languageCode={currentLanguage}
                            onBackToMenu={() => selectMode('menu')}
                            onStartLearning={() => selectMode('learn')}
                            onWordRevised={(_word, isCorrect) => {
                              setCurrentStreak(isCorrect ? prev => prev + 1 : 0);
                              setLastAnswerCorrect(isCorrect);
                            }}
                            onSessionComplete={() => setSessionLearnedWords(prev => new Set([...prev, `revision-${Date.now()}`]))}
                            onUpdateRevisionState={handleUpdateRevisionState}
                            onDueWordsCountChange={(count) => {
                              setReviewDueCount(count);
                              setReviewDue(count > 0);
                              try { localStorage.setItem(`${currentLanguage}-reviewDueCount`, String(count)); } catch {}
                            }}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        );
                        if (mode === 'quiz') return currentLanguage === 'ka' ? (
                          <KamaSigneDirecteurExercise
                            title={t('app.kamaGraphieQuizTitle')}
                            items={graphieReconstructionPractice}
                            onBackToCategories={() => selectMode('menu')}
                            onExerciseComplete={handleSuccess}
                            onAnswer={handleQuizAnswer}
                            timed
                          />
                        ) : (
                          <QuizComponent
                            languageCode={currentLanguage}
                            words={transformedWords}
                            onComplete={handleSuccess}
                            onAnswerSubmit={handleQuizAnswer}
                            backgroundAudioUrl="/ambiance/Chronometre.wav"
                            correctAnswerSoundUrl="/ambiance/Correct.mp3"
                            wrongAnswerSoundUrl="/ambiance/Erreur.mp3"
                            onGameFinished={() => selectMode('menu')}
                          />
                        );
                        if (mode === 'sentenceConstruction') return (
                          <SentenceConstructionGame
                            languageCode={currentLanguage}
                            onBackToCategories={() => { pendingTrailRestore.current = trailScrollY.current; selectMode('learn'); }}
                            onAnswer={handleQuizAnswer}
                            onGameComplete={() => navigateAfterPhrases(constructionFilterCategories)}
                            onSentencesLearned={() => { recordActivity(); }}
                            saveProgressOnSummary={saveProgressOnSummary}
                            userProgress={userProgress}
                            filterCategories={constructionFilterCategories}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        );
                        if (mode === 'sentenceGap') return (
                          <SentenceGapGame
                            languageCode={currentLanguage}
                            onGameComplete={handleSuccess}
                            onBackToCategories={() => selectMode('menu')}
                            onAnswer={handleQuizAnswer}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        );
                        if (mode === 'exercise') return currentLanguage === 'ka' ? (
                          <KamaSigneDirecteurExercise
                            title={t('app.kamaSigneDirecteurTitle')}
                            items={signeDirecteurPractice}
                            onBackToCategories={() => selectMode('menu')}
                            onExerciseComplete={handleSuccess}
                            onAnswer={handleQuizAnswer}
                          />
                        ) : (
                          <ExerciseMode
                            languageCode={currentLanguage}
                            onBackToCategories={() => selectMode('menu')}
                            onExerciseComplete={handleSuccess}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        );
                        if (mode === 'grammar') return (
                          <GrammarMode
                            languageCode={currentLanguage}
                            onBackToCategories={() => { pendingTrailRestore.current = trailScrollY.current; selectMode('learn'); }}
                            onCategoryComplete={handleCategoryComplete}
                            resetToken={resetToken}
                            onWordsLearned={(words) => {
                              handleWordsLearned(words);
                              setSessionLearnedWords(prev => new Set([...prev, ...words.map(w => w.word)]));
                            }}
                            saveProgressOnSummary={saveProgressOnSummary}
                            userProgress={userProgress}
                            isMobileView={false}
                            grammarUnit={grammarUnitFilter}
                            grammarSubcategoryFilter={grammarSubcategoryFilter ?? undefined}
                            onAnswerSubmit={handleQuizAnswer}
                            onStartRevision={() => selectMode('review')}
                            onFilterComplete={() => navigateAfterGrammar(grammarUnitFilter ?? 1, grammarSubcategoryFilter ?? null)}
                            onShare={shareCardData ? () => setShowShareModal(true) : undefined}
                          />
                        );
                        if (mode === 'profile') return (
                          <ProfilePage
                            user={user}
                            userProgress={userProgress}
                            completedCategories={completedCategories}
                            availableCategories={Object.keys(languageData?.categories ?? {}).filter(c => !c.includes('_'))}
                            currentStreak={currentStreak}
                            currentLanguage={currentLanguage}
                            subscriptionTier={subscriptionTier}
                            onLogout={handleLogout}
                            onOpenSubscription={handleOpenSubscription}
                            onResetProgress={handleResetProgress}
                            onChallengeFriend={(friendId) => { setChallengeTargetFriendId(friendId); selectMode('challenge'); }}
                          />
                        );
                        if (mode === 'challenge') return (
                          <ChallengeMode
                            onBackToMenu={() => { setChallengeTargetFriendId(null); backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('menu'); }); }}
                            user={user}
                            initialFriendId={challengeTargetFriendId}
                            languageCode={currentLanguage ?? languageData.code}
                            onOpenMemoryDuel={() => { memoryFromChallenge.current = true; selectMode('memoryDuel'); }}
                          />
                        );
                        if (mode === 'minigames') return (
                          <MiniGames
                            languageCode={currentLanguage}
                            onChallengeFriend={culturalQuizOnly ? undefined : () => selectMode('challenge')}
                            pendingChallengesCount={pendingChallengesCount}
                            onLaunchSongAnalyzer={culturalQuizOnly ? undefined : () => selectMode('songAnalyzer')}
                            onLaunchMemoryDuel={culturalQuizOnly ? undefined : () => selectMode('memoryDuel')}
                            onLaunchForge={culturalQuizOnly ? undefined : () => selectMode('forge')}
                            initialTheme={initialQuizTheme ?? undefined}
                            onBack={() => { setInitialQuizTheme(null); backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('menu'); }); }}
                          />
                        );
                        if (mode === 'dialogue') return (
                          <ConversationGame
                            languageCode={currentLanguage}
                            onBack={() => { pendingTrailRestore.current = trailScrollY.current; setConversationId(null); setLastActiveTab('learn'); selectMode('menu'); }}
                            initialConversationId={conversationId ?? undefined}
                            onComplete={handleConversationComplete}
                          />
                        );
                        if (mode === 'songAnalyzer') return (
                          <SongAnalyzer
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('menu'); })}
                          />
                        );
                        if (mode === 'wordle') return (
                          <WordleGame
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('minigames'); })}
                          />
                        );
                        if (mode === 'weaver') return (
                          <TisserandGame
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { setLastActiveTab('games'); selectMode('menu'); })}
                          />
                        );
                        if (mode === 'forge') return (
                          <ForgeGame
                            languageCode={currentLanguage}
                            onBack={() => backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('minigames'); })}
                          />
                        );
                        if (mode === 'memoryDuel') return (
                          <MemoryDuelGame
                            languageCode={currentLanguage}
                            user={user}
                            onBackToMenu={() => { if (memoryFromChallenge.current) { memoryFromChallenge.current = false; selectMode('challenge'); return; } backFromGame(() => { pendingTrailRestore.current = trailScrollY.current; setLastActiveTab('learn'); selectMode('minigames'); }); }}
                          />
                        );
                        return null;
                      })()}
                    />
                  </div>
                )}

                {/* Mascotte interactive — visible uniquement dans les écrans sentier */}
                {currentLanguage && !isPaymentSuccessPage && mode === 'menu' && (
                  <InteractiveMascot
                    currentCategory={mode}
                    streak={currentStreak}
                    lastAnswerCorrect={lastAnswerCorrect}
                    languageCode={currentLanguage}
                    userName={user}
                    wordsLearned={userProgress.learnedWords.size}
                    sessionWordsLearned={sessionLearnedWords.size}
                  />
                )}

                {showCelebration && <CelebrationAnimation />}
                <AnimatePresence>
                  {categoryCelebration && (
                    <CategoryCelebration
                      key={categoryCelebration.category}
                      category={categoryCelebration.category}
                      onClose={handleCategoryCelebrationClose}
                    />
                  )}
                </AnimatePresence>

                {/* ── Toast : nouveau défi reçu ── */}
                {challengeToast && (
                  <div className="challenge-toast">
                    <div className="challenge-toast__icon"><Picto name="epeesDuel" /></div>
                    <div className="challenge-toast__body">
                      <div className="challenge-toast__title">
                        {challengeToast.count > 1
                          ? t('app.pendingChallengesToast', { count: challengeToast.count })
                          : t('app.newChallengeToast')}
                      </div>
                      <div className="challenge-toast__subtitle">
                        <strong>{challengeToast.name}</strong>
                        {challengeToast.count > 1
                          ? t('app.challengedYouOthers', { count: challengeToast.count - 1 })
                          : t('app.challengedYouSuffix')}
                      </div>
                    </div>
                    <button className="challenge-toast__accept" onClick={() => { setChallengeToast(null); openedFromPractice.current = true; selectMode('challenge'); }}>
                      {t('app.viewChallenge')}
                    </button>
                    <button className="challenge-toast__dismiss" onClick={() => setChallengeToast(null)}>
                      ✕
                    </button>
                  </div>
                )}

                <ConfirmationModal
                  isOpen={showResetConfirmation}
                  onConfirm={confirmResetProgress}
                  onCancel={() => setShowResetConfirmation(false)}
                  message={t('app.resetProgressConfirm')}
                />

                {!isMobileView && <BugReportButton mode={mode} currentLanguage={currentLanguage} />}
              </motion.div>
            )}
 
            <>
              {/* Onboarding — première connexion uniquement */}
              {showOnboarding && currentLanguage && (
                <OnboardingModal
                  onClose={() => setShowOnboarding(false)}
                  languageCode={currentLanguage}
                  onStartFirstLesson={currentLanguage !== 'ka' ? handleStartFirstLesson : undefined}
                />
              )}

              {/* Affichage du modal d'abonnement */}
             <SubscriptionModal
  isOpen={showSubscriptionModal}
  onClose={handleSubscriptionModalClose}
  onSuccess={handleSubscriptionSuccess}
  userEmail={user?.email}
  blockedFeature={blockedFeature ?? undefined}
  context={subscriptionContext}
  streakCount={streakModalCount}
  currentSubscription={subscriptionInfo}
/>

              {/* Animation passage de niveau */}
              <LevelUpAnimation
                show={showLevelUpAnim}
                levelInfo={levelUpInfo}
                onClose={() => setShowLevelUpAnim(false)}
              />

              {/* Animation passage à Premium */}
              <PremiumUnlockedAnimation
                show={showPremiumUnlockedAnim}
                onClose={() => setShowPremiumUnlockedAnim(false)}
              />

              {/* Animation objet de collection débloqué (après les autres modales) */}
              <CollectibleUnlockedAnimation
                collectible={
                  showLevelUpAnim || showPremiumUnlockedAnim || showStreakModal
                    ? null
                    : collectibleQueue[0] ?? null
                }
                onClose={() => setCollectibleQueue(q => q.slice(1))}
              />

              {/* Modal streak quotidien */}
              <DailyStreakAnimation
                show={showStreakModal}
                streakCount={streakModalCount}
                onClose={() => {
                  setShowStreakModal(false);
                  // Upsell aux milestones 7, 14, 30 jours pour les utilisateurs gratuits
                  const isFree = subscriptionTier !== SubscriptionTier.PREMIUM;
                  const isMilestone = [7, 14, 30].includes(streakModalCount);
                  if (isFree && isMilestone) {
                    setSubscriptionContext('streak_milestone');
                    setTimeout(() => setShowSubscriptionModal(true), 600);
                  }
                }}
              />

              {/* Boite de dialogue de confirmation */}
              <ConfirmationModal
                isOpen={showExitConfirmation}
                onConfirm={() => {
                  setShowExitConfirmation(false);
                  setMode('menu');
                }}
                onCancel={() => setShowExitConfirmation(false)}
                message="Etes-vous sur de vouloir quitter ? Votre progression ne sera pas sauvegardee."
              />

              {/* Bannière d'activation des rappels push */}
              <NotificationPermissionBanner />

              {/* Modal de partage */}
              {shareCardData && (
                <ShareProgressModal
                  isOpen={showShareModal}
                  onClose={() => setShowShareModal(false)}
                  data={shareCardData}
                />
              )}
            </>
          </>
          )
        }
      />
    </Routes>
    </>
  </ThemeProvider>
);

};

export default App;